import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    mergeStyles,
    DefaultButton,
    PrimaryButton,
    SearchBox,
    Stack,
    IContextualMenuListProps,
    IRenderFunction,
    Panel,
    PanelType,
    Spinner,
    SpinnerSize,
    Link
} from "@fluentui/react";
import { FilterPill } from "@m365-admin/in-page-filter";
import {
    Box,
    Paper,
    Tab,
    Tabs,
    Typography,
    IconButton,
    Tooltip,
    Button,
    Chip,
    Drawer,
    Divider,
    CircularProgress,
    Link as MuiLink
} from "@mui/material";
import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridFilterModel
} from "@mui/x-data-grid";
import { CustomDataGridToolbar } from "../SharedComponents/CustomDataGrid/CustomDataGridToolbar";
import {
    GridDots20Regular,
    People20Regular,
    Dismiss24Regular,
    Open16Regular,
    Copy16Regular,
    Checkmark16Regular,
    Search20Regular,
    Link20Regular,
    ChatBubblesQuestion24Regular
} from "@fluentui/react-icons";
import { useGetFeatureDetailsQuery, useGetPaginatedFeatureRequestsQuery, useLazyGetPaginatedFeatureRequestsQuery, useGetFiltersQuery, useGetCustomersQuery, useLazyGetCustomersQuery, useCloseTheLoopPrefetch, usePrefetchPanelsMutation } from "../slices/CloseTheLoop.slice";
import { IFeatureEvidence, ICustomerSummary, IFeatureEvidenceFilters, IFeatureDetailsPanelResponse, IPaginatedEvidenceRequest, IFeatureRequestSummary, IEvidenceCustomer } from "../interfaces/CloseTheLoop.interfaces";
import { logEvent, logPageView, startPageLoadTimeTracker, stopPageLoadTimeTracker } from "../helper/AppInsightsHelper";
import { formatDisplayDate } from "../Utils/js-common";
import { CustomerViewTabs } from "../Utils/Enums/Customer.constants";
import Overview from "../components/CloseTheLoop/OverviewTab/Overview";
import IntakeToTriage from "../components/CloseTheLoop/IntakeToTriage";
import PrioritizeAndDecide from "../components/CloseTheLoop/PrioritizeAndDecide";
import CTLExploreDataButton from "../components/CloseTheLoop/utils/CTLExploreDataButton";
import { CTL_FEATURE_EVIDENCES_QUERY, CTL_CUSTOMER_SUMMARY_QUERY } from "../components/CloseTheLoop/utils/ctlKustoQueries";
import CustomDropdown from "../components/ProductView/Common/CustomDropdown";
import FeedbackFormModal from "../components/CloseTheLoop/FeedbackFormModal";
import DCRRequests from "../components/CloseTheLoop/DCRRequests/DCRRequests";
import { THEME } from "../components/CloseTheLoop/utils/theme";
import {
    RichSearchBox,
    CTLFilterPill,
    CTLSearchableFilterPill,
    CTLDateRangeFilterPill,
    ShareFilterButton,
    FILTER_PILL_STYLES
} from "../components/CloseTheLoop/utils/CTLRichControls";

// Theme colors - Security 360 blue theme (shared across CTL tabs via utils/theme)
export { THEME };

const dropdownStyles = {
    root: {
        width: 170
    }
};

// Status configuration - includes all InternalStatus values
const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
    // InternalStatus values from Kusto
    "Evidence Gathering": { label: "Evidence Gathering", color: "#8764B8", bgColor: "#F3EEFB" },
    "In Preview Release": { label: "In Preview", color: "#0078D4", bgColor: "#E5F1FB" },
    "Generally Available": { label: "GA", color: THEME.success, bgColor: THEME.successBg },
    "Not Available": { label: "Not Available", color: THEME.textSecondary, bgColor: "#F0F0F0" },
    "Rejected within CxE triage": { label: "Rejected (CxE)", color: THEME.error, bgColor: THEME.errorBg },
    "In Engineering Backlog": { label: "Eng Backlog", color: "#005A9E", bgColor: "#CCE4F7" },
    "Pending Triage": { label: "Pending Triage", color: THEME.warning, bgColor: THEME.warningBg },
    "Needs More Information": { label: "Needs Info", color: "#E65100", bgColor: "#FFF3E0" },
    "In Development": { label: "In Development", color: "#0078D4", bgColor: "#E5F1FB" },
    "Rejected by Product Group": { label: "Rejected (PG)", color: THEME.error, bgColor: THEME.errorBg },
    // Other common statuses
    "Public Preview": { label: "Public Preview", color: "#0078D4", bgColor: "#E5F1FB" },
    "Private Preview": { label: "Private Preview", color: "#8764B8", bgColor: "#F3EEFB" },
    Approved: { label: "Approved", color: THEME.success, bgColor: THEME.successBg },
    "Under Consideration": { label: "Under Consideration", color: THEME.warning, bgColor: THEME.warningBg },
    Rejected: { label: "Rejected", color: THEME.error, bgColor: THEME.errorBg }
};

// Dynamic color palette for unknown statuses
const DYNAMIC_STATUS_COLORS = [
    { color: "#0078D4", bgColor: "#E5F1FB" },   // Blue
    { color: "#8764B8", bgColor: "#F3EEFB" },   // Purple
    { color: "#107C10", bgColor: "#DFF6DD" },   // Green
    { color: "#E65100", bgColor: "#FFF3E0" },   // Orange
    { color: "#005A9E", bgColor: "#CCE4F7" },   // Dark Blue
    { color: "#C239B3", bgColor: "#FCE4EC" },   // Pink
    { color: "#00838F", bgColor: "#E0F7FA" },   // Teal
];

// Priority configuration
const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
    P0: { label: "P0", color: THEME.error },
    P1: { label: "P1", color: "#E74C3C" },
    P2: { label: "P2", color: THEME.warning },
    P3: { label: "P3", color: THEME.textSecondary }
};

// Stage configuration
const STAGE_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
    GA: { label: "GA", color: THEME.success, bgColor: THEME.successBg },
    "Public Preview": { label: "Public Preview", color: "#0078D4", bgColor: "#E5F1FB" },
    "Private Preview": { label: "Private Preview", color: "#8764B8", bgColor: "#F3EEFB" }
};

/**
 * Hook for responsive DataGrid sizing
 * Calculates appropriate height and page size based on viewport
 * Ensures pagination footer is always visible
 */
const useResponsiveGrid = (offsetFromTop: number = 420) => {
    const [dimensions, setDimensions] = useState(() => {
        const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
        return calculateDimensions(viewportHeight, offsetFromTop);
    });

    useEffect(() => {
        const handleResize = () => {
            const viewportHeight = window.innerHeight;
            setDimensions(calculateDimensions(viewportHeight, offsetFromTop));
        };

        window.addEventListener('resize', handleResize);
        // Initial calculation
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, [offsetFromTop]);

    return dimensions;
};

/**
 * Calculate grid dimensions based on viewport height
 * Row height ~36px (compact), header ~56px, toolbar ~52px, pagination ~52px
 */
const calculateDimensions = (viewportHeight: number, offsetFromTop: number) => {
    // Reserve extra buffer (100px) to ensure footer is never cut off even at high zoom
    const availableHeight = viewportHeight - offsetFromTop - 100;
    const headerAndToolbar = 108; // toolbar + column header
    const paginationFooter = 56; // pagination row
    const minGridHeight = 200; // minimum usable grid height

    // Calculate grid height - ensure minimum
    const gridHeight = Math.max(availableHeight, minGridHeight);

    // Calculate how many rows can fit (row height in compact mode is ~36px)
    const rowHeight = 36;
    const contentHeight = gridHeight - headerAndToolbar - paginationFooter;
    const visibleRows = Math.floor(contentHeight / rowHeight);

    // Determine appropriate page size based on visible rows (minimum 25)
    let defaultPageSize: number;
    if (visibleRows <= 30) {
        defaultPageSize = 25;
    } else if (visibleRows <= 55) {
        defaultPageSize = 50;
    } else {
        defaultPageSize = 100;
    }

    return {
        gridHeight,
        defaultPageSize,
        visibleRows
    };
};

/**
 * Feature Details Panel Component - Rich side panel for feature request details
 */
interface FeatureDetailsPanelProps {
    isOpen: boolean;
    onClose: () => void;
    featureRequestIdWithSource: string | null;
    showCustomersSection?: boolean;
}

export const FeatureDetailsPanel: React.FC<FeatureDetailsPanelProps> = ({
    isOpen,
    onClose,
    featureRequestIdWithSource,
    showCustomersSection = true
}) => {
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [isCustomersExpanded, setIsCustomersExpanded] = useState(false);
    const [visibleCustomersCount, setVisibleCustomersCount] = useState(10);
    const [descriptionTab, setDescriptionTab] = useState(0);
    const [copiedShareLink, setCopiedShareLink] = useState(false);

    const { data: details, isLoading, error, isFetching } = useGetFeatureDetailsQuery(
        featureRequestIdWithSource || "",
        { skip: !featureRequestIdWithSource || !isOpen }
    );

    // Reset expansion state when panel opens with new feature
    useEffect(() => {
        if (!isOpen) {
            setIsCustomersExpanded(false);
            setVisibleCustomersCount(10);
            setDescriptionTab(0);
            setCopiedShareLink(false);
        }
    }, [isOpen, featureRequestIdWithSource]);

    // Handle share panel link
    const handleSharePanel = useCallback(() => {
        if (!featureRequestIdWithSource) return;
        const url = new URL(window.location.href);
        url.searchParams.set("panel", featureRequestIdWithSource);
        navigator.clipboard.writeText(url.toString());
        setCopiedShareLink(true);
        setTimeout(() => setCopiedShareLink(false), 2000);
    }, [featureRequestIdWithSource]);

    const handleCopy = useCallback((text: string, field: string) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    }, []);

    // Extract data from API response
    const displayEvidence = details?.evidence;
    const customers = details?.customers || [];
    // Sort evidences by createdDate (latest first)
    const allEvidences = useMemo(() => {
        const evidences = details?.allEvidences || [];
        return [...evidences].sort((a, b) => {
            const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
            const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
            return dateB - dateA; // Latest first
        });
    }, [details?.allEvidences]);
    const evidenceCount = details?.evidenceCount || 0;
    const lifecycle = details?.lifecycle;
    const mapping = details?.mapping;

    // Check if we have data to display
    const hasData = !isLoading && !isFetching && details && displayEvidence;

    // Get normalized status color - uses same colors as StatusChip
    const getStatusColor = (status: string): string => {
        if (!status) return THEME.textSecondary;
        // Check exact match first
        if (STATUS_CONFIG[status]) return STATUS_CONFIG[status].color;
        // Check partial match
        const partialMatch = Object.keys(STATUS_CONFIG).find((key) =>
            status.toLowerCase().includes(key.toLowerCase())
        );
        if (partialMatch) return STATUS_CONFIG[partialMatch].color;
        // Generate consistent color for unknown status
        const hash = status.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return DYNAMIC_STATUS_COLORS[hash % DYNAMIC_STATUS_COLORS.length].color;
    };

    // Get status background color
    const getStatusBgColor = (status: string): string => {
        if (!status) return "#F0F0F0";
        if (STATUS_CONFIG[status]) return STATUS_CONFIG[status].bgColor;
        const partialMatch = Object.keys(STATUS_CONFIG).find((key) =>
            status.toLowerCase().includes(key.toLowerCase())
        );
        if (partialMatch) return STATUS_CONFIG[partialMatch].bgColor;
        const hash = status.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return DYNAMIC_STATUS_COLORS[hash % DYNAMIC_STATUS_COLORS.length].bgColor;
    };

    // Get status label
    const getStatusLabel = (status: string): string => {
        if (!status) return "Unknown";
        if (STATUS_CONFIG[status]) return STATUS_CONFIG[status].label;
        const partialMatch = Object.keys(STATUS_CONFIG).find((key) =>
            status.toLowerCase().includes(key.toLowerCase())
        );
        if (partialMatch) return STATUS_CONFIG[partialMatch].label;
        return status.length > 20 ? status.substring(0, 18) + "..." : status;
    };

    // Get priority color
    const getPriorityColor = (priority: string) => {
        return PRIORITY_CONFIG[priority]?.color || THEME.textSecondary;
    };

    const formatDate = formatDisplayDate;

    // Parse tags
    const tags = useMemo(() => {
        if (!displayEvidence?.tags) return [];
        return displayEvidence.tags.split(",").map((t) => t.trim()).filter((t) => t);
    }, [displayEvidence?.tags]);

    // Check if URL is valid
    const isValidUrl = (url: string | null | undefined): boolean => {
        if (!url || url.trim() === "" || url === "#") return false;
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: "100%", sm: 560, md: 600 },
                    maxWidth: "100%",
                    boxShadow: "-8px 0 32px rgba(0, 0, 0, 0.15)",
                    display: "flex",
                    flexDirection: "column"
                }
            }}
        >
            {/* Loading State - Full Panel Loader */}
            {(isLoading || isFetching) && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fff",
                        zIndex: 10
                    }}
                >
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            backgroundColor: "#F0F6FF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2
                        }}
                    >
                        <CircularProgress size={32} sx={{ color: THEME.primary }} />
                    </Box>
                    <Typography
                        sx={{
                            color: THEME.textPrimary,
                            fontSize: "14px",
                            fontWeight: 500,
                            fontFamily: "'Segoe UI', sans-serif"
                        }}
                    >
                        Loading feature details...
                    </Typography>
                    <Typography
                        sx={{
                            color: THEME.textSecondary,
                            fontSize: "12px",
                            mt: 0.5,
                            fontFamily: "'Segoe UI', sans-serif"
                        }}
                    >
                        Please wait
                    </Typography>
                </Box>
            )}

            {/* Error State */}
            {error && !isLoading && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fff",
                        zIndex: 10,
                        p: 4
                    }}
                >
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            backgroundColor: "#FEE2E2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2
                        }}
                    >
                        <Dismiss24Regular style={{ color: THEME.error, fontSize: 28 }} />
                    </Box>
                    <Typography
                        sx={{
                            color: THEME.error,
                            fontSize: "14px",
                            fontWeight: 500,
                            fontFamily: "'Segoe UI', sans-serif"
                        }}
                    >
                        Failed to load details
                    </Typography>
                    <Typography
                        sx={{
                            color: THEME.textSecondary,
                            fontSize: "12px",
                            mt: 0.5,
                            fontFamily: "'Segoe UI', sans-serif"
                        }}
                    >
                        Please try again
                    </Typography>
                    <Button
                        onClick={onClose}
                        sx={{
                            mt: 2,
                            textTransform: "none",
                            color: THEME.primary
                        }}
                    >
                        Close
                    </Button>
                </Box>
            )}

            {/* Content - Only show when data is loaded */}
            {hasData && (
                <>
                    {/* Panel Header */}
                    <Box
                        sx={{
                            p: 3,
                            borderBottom: `1px solid ${THEME.border}`,
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            backgroundColor: "#FAFBFC"
                        }}
                    >
                        <Box sx={{ flex: 1, pr: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                <Typography
                                    component="a"
                                    href={displayEvidence?.featureRequestURL || "#"}
                                    target="_blank"
                                    sx={{
                                        color: THEME.primary,
                                        fontWeight: 600,
                                        fontSize: "13px",
                                        fontFamily: "'Segoe UI', sans-serif",
                                        textDecoration: "none",
                                        "&:hover": { textDecoration: "underline" },
                                        cursor: isValidUrl(displayEvidence?.featureRequestURL) ? "pointer" : "default"
                                    }}
                                    onClick={(e) => {
                                        if (!isValidUrl(displayEvidence?.featureRequestURL)) {
                                            e.preventDefault();
                                        }
                                    }}
                                >
                                    {displayEvidence?.featureRequestId || "—"}
                                </Typography>
                                <Chip
                                    label={getStatusLabel(displayEvidence?.status || "")}
                                    size="small"
                                    title={displayEvidence?.status || ""}
                                    sx={{
                                        height: 22,
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        backgroundColor: getStatusBgColor(displayEvidence?.status || ""),
                                        color: getStatusColor(displayEvidence?.status || ""),
                                        "& .MuiChip-label": { px: 1.5 }
                                    }}
                                />
                                <Chip
                                    label={displayEvidence?.priority || "—"}
                                    size="small"
                                    sx={{
                                        height: 22,
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        backgroundColor: `${getPriorityColor(displayEvidence?.priority || "")}15`,
                                        color: getPriorityColor(displayEvidence?.priority || ""),
                                        "& .MuiChip-label": { px: 1.5 }
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: THEME.textPrimary,
                                    fontSize: "17px",
                                    lineHeight: 1.4,
                                    fontFamily: "'Segoe UI', sans-serif"
                                }}
                            >
                                {displayEvidence?.title || "—"}
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1, mt: -0.5, mr: -0.5, alignItems: "center" }}>
                            <Tooltip title={copiedShareLink ? "Link copied to clipboard!" : "Copy shareable link to this item"}>
                                <Button
                                    onClick={handleSharePanel}
                                    variant="outlined"
                                    size="small"
                                    startIcon={copiedShareLink ? <Checkmark16Regular /> : <Link20Regular />}
                                    sx={{
                                        px: 1.5,
                                        py: 0.5,
                                        minWidth: "auto",
                                        textTransform: "none",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        borderRadius: "6px",
                                        backgroundColor: copiedShareLink ? "#DFF6DD" : "#E7F1FF",
                                        borderColor: copiedShareLink ? THEME.success : THEME.primary,
                                        color: copiedShareLink ? THEME.success : THEME.primary,
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                            backgroundColor: copiedShareLink ? "#C8EBC5" : "#D0E4FF",
                                            borderColor: copiedShareLink ? THEME.success : THEME.primaryDark
                                        }
                                    }}
                                >
                                    {copiedShareLink ? "Copied!" : "Share"}
                                </Button>
                            </Tooltip>
                            <IconButton
                                onClick={onClose}
                                size="small"
                                sx={{
                                    backgroundColor: "#E5E7EB",
                                    "&:hover": { backgroundColor: "#D1D5DB" }
                                }}
                            >
                                <Dismiss24Regular />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Panel Content */}
                    <Box sx={{ p: 3, overflowY: "auto", flex: 1, fontFamily: "'Segoe UI', sans-serif" }}>
                        {/* Summary Stats - Compact Rich Cards */}
                        <Box sx={{ display: "flex", gap: 1.5, mb: 3 }}>
                            <Box
                                sx={{
                                    flex: 1,
                                    p: 1.5,
                                    background: "linear-gradient(135deg, #E5F1FB 0%, #F0F7FF 100%)",
                                    borderRadius: "10px",
                                    border: "1px solid #B3D7FC",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    boxShadow: "0 2px 8px rgba(0, 120, 212, 0.08)"
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: "10px",
                                        background: "linear-gradient(135deg, #0078D4 0%, #2B88D8 100%)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        boxShadow: "0 2px 6px rgba(0, 120, 212, 0.3)"
                                    }}
                                >
                                    <GridDots20Regular style={{ color: "#fff" }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: "20px", fontWeight: 700, color: THEME.primary, lineHeight: 1.1 }}>
                                        {evidenceCount}
                                    </Typography>
                                    <Typography sx={{ fontSize: "11px", color: THEME.textSecondary, fontWeight: 500 }}>
                                        Evidence{evidenceCount !== 1 ? "s" : ""}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    p: 1.5,
                                    background: "linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)",
                                    borderRadius: "10px",
                                    border: "1px solid #A5D6A7",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    boxShadow: "0 2px 8px rgba(16, 124, 16, 0.08)"
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: "10px",
                                        background: "linear-gradient(135deg, #107C10 0%, #2E8540 100%)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        boxShadow: "0 2px 6px rgba(16, 124, 16, 0.3)"
                                    }}
                                >
                                    <People20Regular style={{ color: "#fff" }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: "20px", fontWeight: 700, color: THEME.success, lineHeight: 1.1 }}>
                                        {customers.length}
                                    </Typography>
                                    <Typography sx={{ fontSize: "11px", color: THEME.textSecondary, fontWeight: 500 }}>
                                        Customer{customers.length !== 1 ? "s" : ""}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Earliest Customer Info */}
                        {customers.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography sx={{ fontSize: "11px", color: THEME.textSecondary, mb: 0.5, fontWeight: 500 }}>
                                    Earliest Customer
                                </Typography>
                                <Box
                                    sx={{
                                        p: 2,
                                        backgroundColor: "#FAFBFC",
                                        borderRadius: "10px",
                                        border: `1px solid ${THEME.border}`,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: "50%",
                                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#fff",
                                            fontSize: "16px",
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)"
                                        }}
                                    >
                                        {(customers[0].customerName || customers[0].tenantName || "?").charAt(0)}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: 600,
                                                color: THEME.textPrimary,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap"
                                            }}
                                        >
                                            {customers[0].customerName || customers[0].tenantName || "Unknown Customer"}
                                        </Typography>
                                        <Typography sx={{ fontSize: "11px", color: THEME.textSecondary }}>
                                            {customers[0].industry || customers[0].region || ""}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Open Feature Request Button */}
                        {isValidUrl(displayEvidence?.featureRequestURL) && (
                            <Button
                                variant="contained"
                                startIcon={<Open16Regular />}
                                onClick={() => window.open(displayEvidence?.featureRequestURL, "_blank")}
                                sx={{
                                    mb: 3,
                                    textTransform: "none",
                                    backgroundColor: THEME.primary,
                                    fontWeight: 500,
                                    fontSize: "13px",
                                    borderRadius: "8px",
                                    px: 2.5,
                                    py: 1,
                                    boxShadow: "0 2px 4px rgba(0, 120, 212, 0.2)",
                                    "&:hover": {
                                        backgroundColor: THEME.primaryDark,
                                        boxShadow: "0 4px 8px rgba(0, 120, 212, 0.3)"
                                    }
                                }}
                            >
                                Open feature request {displayEvidence?.featureRequestId}
                            </Button>
                        )}

                        {/* Reference IDs Section - Display all mappings grouped by source */}
                        <Box sx={{ mb: 3 }}>
                            <Typography
                                variant="overline"
                                sx={{
                                    color: THEME.textSecondary,
                                    fontWeight: 600,
                                    fontSize: "11px",
                                    letterSpacing: "0.5px",
                                    mb: 1.5,
                                    display: "block"
                                }}
                            >
                                REFERENCE IDS
                            </Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 1.5 }}>
                                {/* CRM */}
                                <Box sx={{ p: 1.5, backgroundColor: "#F8F9FA", borderRadius: "8px", border: `1px solid ${THEME.border}` }}>
                                    <Typography sx={{ fontSize: "10px", color: THEME.textSecondary, mb: 0.5 }}>CRM</Typography>
                                    {(() => {
                                        const crmMappings = mapping?.mappings?.filter(m => m.source === "CRM") || [];
                                        if (crmMappings.length === 0) return <Typography sx={{ fontSize: "11px", color: THEME.textSecondary }}>—</Typography>;
                                        return crmMappings.map((m, i) => (
                                            <Typography key={i} component="a" href={m.url} target="_blank"
                                                sx={{ fontSize: "11px", color: THEME.primary, textDecoration: "none", display: "block", "&:hover": { textDecoration: "underline" } }}>
                                                {m.sourceFeatureRequestId}
                                            </Typography>
                                        ));
                                    })()}
                                </Box>
                                {/* UAT Feature */}
                                <Box sx={{ p: 1.5, backgroundColor: "#F8F9FA", borderRadius: "8px", border: `1px solid ${THEME.border}` }}>
                                    <Typography sx={{ fontSize: "10px", color: THEME.textSecondary, mb: 0.5 }}>UAT Feature</Typography>
                                    {(() => {
                                        const uatMappings = mapping?.mappings?.filter(m => m.source === "UAT") || [];
                                        if (uatMappings.length === 0) return <Typography sx={{ fontSize: "11px", color: THEME.textSecondary }}>—</Typography>;
                                        return uatMappings.map((m, i) => (
                                            <Typography key={i} component="a" href={m.url} target="_blank"
                                                sx={{ fontSize: "11px", color: THEME.primary, textDecoration: "none", display: "block", "&:hover": { textDecoration: "underline" } }}>
                                                {m.sourceFeatureRequestId}
                                            </Typography>
                                        ));
                                    })()}
                                </Box>
                                {/* CSS */}
                                <Box sx={{ p: 1.5, backgroundColor: "#F8F9FA", borderRadius: "8px", border: `1px solid ${THEME.border}` }}>
                                    <Typography sx={{ fontSize: "10px", color: THEME.textSecondary, mb: 0.5 }}>CSS</Typography>
                                    {(() => {
                                        const cssMappings = mapping?.mappings?.filter(m => m.source === "CSS") || [];
                                        const hasCaseNumber = displayEvidence?.caseNumber && displayEvidence.caseNumber.trim() !== "";

                                        if (cssMappings.length === 0 && !hasCaseNumber) {
                                            return <Typography sx={{ fontSize: "11px", color: THEME.textSecondary }}>—</Typography>;
                                        }

                                        return (
                                            <Box>
                                                {cssMappings.length > 0 ? cssMappings.map((m, i) => (
                                                    <Typography key={i} component="a" href={m.url} target="_blank"
                                                        sx={{ fontSize: "11px", color: THEME.primary, textDecoration: "none", display: "block", "&:hover": { textDecoration: "underline" } }}>
                                                        {m.sourceFeatureRequestId}
                                                    </Typography>
                                                )) : null}
                                                {hasCaseNumber && (
                                                    <Typography sx={{ fontSize: "11px", color: THEME.textPrimary, fontWeight: 500 }}>
                                                        {displayEvidence.caseNumber}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })()}
                                </Box>
                                {/* DCR */}
                                <Box sx={{ p: 1.5, backgroundColor: "#F8F9FA", borderRadius: "8px", border: `1px solid ${THEME.border}` }}>
                                    <Typography sx={{ fontSize: "10px", color: THEME.textSecondary, mb: 0.5 }}>DCR</Typography>
                                    {mapping?.dcr_Id && isValidUrl(mapping?.dcr_URL) ? (
                                        <Typography component="a" href={mapping.dcr_URL ?? undefined} target="_blank"
                                            sx={{ fontSize: "11px", color: THEME.primary, textDecoration: "none", display: "block", fontWeight: 500, "&:hover": { textDecoration: "underline" } }}>
                                            {mapping.dcr_Id}
                                        </Typography>
                                    ) : mapping?.dcr_Id ? (
                                        <Typography sx={{ fontSize: "11px", color: THEME.primary }}>{mapping.dcr_Id}</Typography>
                                    ) : (
                                        <Typography sx={{ fontSize: "11px", color: THEME.textSecondary }}>—</Typography>
                                    )}
                                </Box>
                                {/* Engineering ADO - Only shown in Review & Analyze tab */}
                                {showCustomersSection && (
                                    <Box sx={{ p: 1.5, backgroundColor: "#E8F5E9", borderRadius: "8px", border: `1px solid #A5D6A7` }}>
                                        <Typography sx={{ fontSize: "10px", color: THEME.success, mb: 0.5, fontWeight: 600 }}>Engineering ADO</Typography>
                                        {mapping?.engineeringAdo_Id && isValidUrl(mapping?.engineeringAdo_URL) ? (
                                            <Typography component="a" href={mapping.engineeringAdo_URL ?? undefined} target="_blank"
                                                sx={{ fontSize: "11px", color: THEME.success, textDecoration: "none", display: "block", fontWeight: 500, "&:hover": { textDecoration: "underline" } }}>
                                                {mapping.engineeringAdo_Id}
                                            </Typography>
                                        ) : mapping?.engineeringAdo_Id ? (
                                            <Typography sx={{ fontSize: "11px", color: THEME.success, fontWeight: 500 }}>{mapping.engineeringAdo_Id}</Typography>
                                        ) : (
                                            <Typography sx={{ fontSize: "11px", color: THEME.textSecondary }}>—</Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Feature Lifecycle */}
                        <Box sx={{ mb: 3 }}>
                            <Typography
                                variant="overline"
                                sx={{
                                    color: THEME.textSecondary,
                                    fontWeight: 600,
                                    fontSize: "11px",
                                    letterSpacing: "0.5px",
                                    mb: 2,
                                    display: "block"
                                }}
                            >
                                FEATURE LIFECYCLE
                            </Typography>
                            {lifecycle?.stages && lifecycle.stages.length > 0 ? (
                                <Box
                                    sx={{
                                        background: "linear-gradient(135deg, #F8FAFC 0%, #EEF2F7 100%)",
                                        borderRadius: "12px",
                                        p: 2,
                                        border: `1px solid ${THEME.border}`
                                    }}
                                >
                                    {/* Progress Bar */}
                                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                        <Box
                                            sx={{
                                                flex: 1,
                                                height: 6,
                                                backgroundColor: "#E1E5EB",
                                                borderRadius: 3,
                                                overflow: "hidden"
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: `${(lifecycle.currentStageIndex / lifecycle.stages.length) * 100}%`,
                                                    height: "100%",
                                                    background: `linear-gradient(90deg, ${THEME.primary} 0%, ${THEME.primaryLight} 100%)`,
                                                    borderRadius: 3
                                                }}
                                            />
                                        </Box>
                                        <Typography sx={{ ml: 1.5, fontSize: "11px", fontWeight: 600, color: THEME.primary }}>
                                            {lifecycle.currentStageIndex}/{lifecycle.stages.length}
                                        </Typography>
                                    </Box>
                                    {/* Timeline Steps */}
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                        {lifecycle.stages.map((stage, idx) => {
                                            const isCurrent = stage.transitionOrder === lifecycle.currentStageIndex;
                                            const isCompleted = stage.transitionOrder < lifecycle.currentStageIndex;
                                            return (
                                                <Box key={stage.transitionOrder} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                        <Box
                                                            sx={{
                                                                width: 24,
                                                                height: 24,
                                                                borderRadius: "50%",
                                                                backgroundColor: isCompleted ? THEME.success : isCurrent ? THEME.primary : "#E1E5EB",
                                                                color: isCompleted || isCurrent ? "#fff" : THEME.textSecondary,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontWeight: 700,
                                                                fontSize: "10px"
                                                            }}
                                                        >
                                                            {isCompleted ? "✓" : stage.transitionOrder}
                                                        </Box>
                                                        {idx < lifecycle.stages.length - 1 && (
                                                            <Box sx={{ width: 2, height: 16, backgroundColor: isCompleted ? THEME.success : "#E1E5EB", mt: 0.5 }} />
                                                        )}
                                                    </Box>
                                                    <Box sx={{ flex: 1, pt: 0.25 }}>
                                                        <Typography sx={{ fontSize: "12px", fontWeight: isCurrent ? 600 : 400, color: THEME.textPrimary }}>
                                                            {stage.currentCustomerFacingStatus}
                                                        </Typography>
                                                        {stage.startDate && (
                                                            <Typography sx={{ fontSize: "10px", color: THEME.textSecondary }}>
                                                                {formatDate(stage.startDate)} {stage.daysInStatus > 0 && `• ${stage.daysInStatus} days`}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ p: 3, backgroundColor: "#F8F9FA", borderRadius: "8px", textAlign: "center" }}>
                                    <Typography sx={{ fontSize: "12px", color: THEME.textSecondary, fontStyle: "italic" }}>
                                        No lifecycle data available
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Product & Additional Info */}
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: "grid", gridTemplateColumns: showCustomersSection ? "1fr 1fr 1fr" : "1fr 1fr", gap: 2 }}>
                            <Box>
                                <Typography sx={{ fontSize: "11px", color: THEME.textSecondary, fontWeight: 600, mb: 0.5 }}>PRODUCT</Typography>
                                <Chip label={displayEvidence?.productName || "—"} size="small" sx={{ backgroundColor: "#E5F1FB", color: THEME.primary, fontWeight: 500, fontSize: "12px" }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: "11px", color: THEME.textSecondary, fontWeight: 600, mb: 0.5 }}>SOURCE</Typography>
                                <Chip label={displayEvidence?.source || "—"} size="small" sx={{ backgroundColor: "#F5F5F5", color: THEME.textPrimary, fontWeight: 500, fontSize: "12px" }} />
                            </Box>
                            {/* Engineering PM - Only shown in Review & Analyze tab */}
                            {showCustomersSection && (
                                <Box>
                                    <Typography sx={{ fontSize: "11px", color: THEME.textSecondary, fontWeight: 600, mb: 0.5 }}>ENGINEERING PM</Typography>
                                    <Typography sx={{ fontSize: "12px", color: details?.engineeringPM ? THEME.textPrimary : THEME.textSecondary, fontWeight: 500 }}>
                                        {details?.engineeringPM || "—"}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Description & Triage Comments - Tabbed Section */}
                        {(details?.description || details?.featureTriageComments || details?.customerFacingDescription) && (
                            <Box sx={{ mt: 3 }}>
                                <Box
                                    sx={{
                                        borderRadius: "12px",
                                        border: `1px solid ${THEME.border}`,
                                        overflow: "hidden",
                                        backgroundColor: "#FAFBFC"
                                    }}
                                >
                                    {/* Tab Header */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            borderBottom: `1px solid ${THEME.border}`,
                                            backgroundColor: "#F5F7F9"
                                        }}
                                    >
                                        {[
                                            { label: "Description", hasContent: !!details?.description },
                                            { label: "Triage Comments", hasContent: !!details?.featureTriageComments },
                                            { label: "Customer Facing", hasContent: !!details?.customerFacingDescription }
                                        ].map((tab, index) => (
                                            <Box
                                                key={tab.label}
                                                onClick={() => tab.hasContent && setDescriptionTab(index)}
                                                sx={{
                                                    flex: 1,
                                                    py: 1.5,
                                                    px: 2,
                                                    textAlign: "center",
                                                    cursor: tab.hasContent ? "pointer" : "not-allowed",
                                                    opacity: tab.hasContent ? 1 : 0.4,
                                                    backgroundColor: descriptionTab === index ? "#fff" : "transparent",
                                                    borderBottom: descriptionTab === index ? `2px solid ${THEME.primary}` : "2px solid transparent",
                                                    transition: "all 0.2s ease",
                                                    "&:hover": tab.hasContent ? {
                                                        backgroundColor: descriptionTab === index ? "#fff" : "#EDF1F5"
                                                    } : {}
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: "12px",
                                                        fontWeight: descriptionTab === index ? 600 : 500,
                                                        color: descriptionTab === index ? THEME.primary : THEME.textSecondary,
                                                        fontFamily: "'Segoe UI', sans-serif"
                                                    }}
                                                >
                                                    {tab.label}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>

                                    {/* Tab Content */}
                                    <Box
                                        sx={{
                                            p: 2.5,
                                            backgroundColor: "#fff",
                                            minHeight: 80,
                                            maxHeight: 200,
                                            overflowY: "auto",
                                            "&::-webkit-scrollbar": { width: "6px" },
                                            "&::-webkit-scrollbar-track": { background: "#F5F5F5" },
                                            "&::-webkit-scrollbar-thumb": { background: "#C1C1C1", borderRadius: "3px" },
                                            "&::-webkit-scrollbar-thumb:hover": { background: "#A1A1A1" }
                                        }}
                                    >
                                        {descriptionTab === 0 && (
                                            <Typography
                                                sx={{
                                                    fontSize: "13px",
                                                    color: THEME.textPrimary,
                                                    lineHeight: 1.6,
                                                    fontFamily: "'Segoe UI', sans-serif",
                                                    whiteSpace: "pre-wrap"
                                                }}
                                            >
                                                {details?.description || "No description available"}
                                            </Typography>
                                        )}
                                        {descriptionTab === 1 && (
                                            <Typography
                                                sx={{
                                                    fontSize: "13px",
                                                    color: THEME.textPrimary,
                                                    lineHeight: 1.6,
                                                    fontFamily: "'Segoe UI', sans-serif",
                                                    whiteSpace: "pre-wrap"
                                                }}
                                            >
                                                {details?.featureTriageComments || "No triage comments available"}
                                            </Typography>
                                        )}
                                        {descriptionTab === 2 && (
                                            <Typography
                                                sx={{
                                                    fontSize: "13px",
                                                    color: THEME.textPrimary,
                                                    lineHeight: 1.6,
                                                    fontFamily: "'Segoe UI', sans-serif",
                                                    whiteSpace: "pre-wrap"
                                                }}
                                            >
                                                {details?.customerFacingDescription || "No customer facing description available"}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Expandable Requesting Customers Section - Only shown in Review & Analyze tab */}
                        {showCustomersSection && allEvidences.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Box
                                    onClick={() => setIsCustomersExpanded(!isCustomersExpanded)}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        cursor: "pointer",
                                        p: 1.5,
                                        backgroundColor: "#F8F9FA",
                                        borderRadius: isCustomersExpanded ? "8px 8px 0 0" : "8px",
                                        border: `1px solid ${THEME.border}`,
                                        borderBottom: isCustomersExpanded ? "none" : `1px solid ${THEME.border}`,
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                            backgroundColor: "#F0F4F8"
                                        }
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: "8px",
                                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxShadow: "0 2px 4px rgba(102, 126, 234, 0.2)"
                                            }}
                                        >
                                            <People20Regular style={{ color: "#fff", width: 18, height: 18 }} />
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: THEME.textPrimary }}>
                                                Requesting Customers/Evidence
                                            </Typography>
                                            <Typography sx={{ fontSize: "11px", color: THEME.textSecondary }}>
                                                {isCustomersExpanded ? "Click to collapse" : `${allEvidences.length} evidence records`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            transform: isCustomersExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                            transition: "transform 0.2s ease",
                                            color: THEME.textSecondary
                                        }}
                                    >
                                        <span style={{ fontSize: "12px" }}>▼</span>
                                    </Box>
                                </Box>

                                {/* Expanded Evidence List with Scroll */}
                                {isCustomersExpanded && (
                                    <Box
                                        sx={{
                                            border: `1px solid ${THEME.border}`,
                                            borderTop: "none",
                                            borderRadius: "0 0 8px 8px",
                                            overflow: "hidden",
                                            display: "flex",
                                            flexDirection: "column"
                                        }}
                                    >
                                        {/* Scrollable Evidence List */}
                                        <Box
                                            sx={{
                                                maxHeight: 400,
                                                overflowY: "auto",
                                                "&::-webkit-scrollbar": {
                                                    width: "6px"
                                                },
                                                "&::-webkit-scrollbar-track": {
                                                    background: "#F5F5F5"
                                                },
                                                "&::-webkit-scrollbar-thumb": {
                                                    background: "#C1C1C1",
                                                    borderRadius: "3px"
                                                },
                                                "&::-webkit-scrollbar-thumb:hover": {
                                                    background: "#A1A1A1"
                                                }
                                            }}
                                        >
                                            {allEvidences.slice(0, visibleCustomersCount).map((evidence, index) => (
                                                <Box
                                                    key={`${evidence.featureEvidenceId}-${index}`}
                                                    sx={{
                                                        px: 1.5,
                                                        py: 1.25,
                                                        backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#FAFBFC",
                                                        borderBottom: index < Math.min(allEvidences.length, visibleCustomersCount) - 1 ? `1px solid ${THEME.border}` : "none"
                                                    }}
                                                >
                                                    {/* Row 1: Avatar + Customer Name + Priority */}
                                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                            <Box
                                                                sx={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    borderRadius: "50%",
                                                                    background: `linear-gradient(135deg, ${["#667eea", "#764ba2", "#0078D4", "#107C10", "#E65100"][index % 5]} 0%, ${["#764ba2", "#667eea", "#2B88D8", "#2E8540", "#FF8F00"][index % 5]} 100%)`,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    color: "#fff",
                                                                    fontSize: "12px",
                                                                    fontWeight: 700,
                                                                    textTransform: "uppercase",
                                                                    flexShrink: 0
                                                                }}
                                                            >
                                                                {(evidence.tpAccountName || evidence.tenantName || "?").charAt(0)}
                                                            </Box>
                                                            <Box>
                                                                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: THEME.textPrimary, lineHeight: 1.2 }}>
                                                                    {evidence.tpAccountName || evidence.tenantName || "Unknown Customer"}
                                                                </Typography>
                                                                {evidence.industry && (
                                                                    <Typography sx={{ fontSize: "10px", color: THEME.success, fontWeight: 500 }}>
                                                                        {evidence.industry}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        {evidence.priority && (
                                                            <Chip
                                                                label={evidence.priority}
                                                                size="small"
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: "10px",
                                                                    fontWeight: 600,
                                                                    backgroundColor: evidence.priority === "P0" ? "#FDE7E9" : evidence.priority === "P1" ? "#FFEBE6" : evidence.priority === "P2" ? "#FFF4CE" : "#F5F5F5",
                                                                    color: evidence.priority === "P0" ? THEME.error : evidence.priority === "P1" ? "#E74C3C" : evidence.priority === "P2" ? "#B8860B" : THEME.textSecondary
                                                                }}
                                                            />
                                                        )}
                                                    </Box>

                                                    {/* Row 2: Region | Created | Evidence Link */}
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: 5, color: THEME.textSecondary, fontSize: "10px" }}>
                                                        {evidence.region && (
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                                                                <span>📍</span>
                                                                <Typography sx={{ fontSize: "10px" }}>{evidence.region}</Typography>
                                                            </Box>
                                                        )}
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                                                            <span>📅</span>
                                                            <Typography sx={{ fontSize: "10px" }}>{formatDate(evidence.createdDate)}</Typography>
                                                        </Box>
                                                        {isValidUrl(evidence.featureEvidenceURL) ? (
                                                            <Typography
                                                                component="a"
                                                                href={evidence.featureEvidenceURL}
                                                                target="_blank"
                                                                sx={{ fontSize: "10px", color: THEME.primary, textDecoration: "none", fontWeight: 500, "&:hover": { textDecoration: "underline" } }}
                                                            >
                                                                {evidence.featureEvidenceId} ↗
                                                            </Typography>
                                                        ) : (
                                                            <Typography sx={{ fontSize: "10px", color: THEME.textSecondary }}>{evidence.featureEvidenceId}</Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>

                                        {/* View More / Collapse Buttons - Always visible at bottom */}
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                backgroundColor: "#F8F9FA",
                                                borderTop: `1px solid ${THEME.border}`,
                                                display: "flex",
                                                justifyContent: "center",
                                                gap: 2
                                            }}
                                        >
                                            {visibleCustomersCount < allEvidences.length && (
                                                <Button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setVisibleCustomersCount(prev => Math.min(prev + 10, allEvidences.length));
                                                    }}
                                                    sx={{
                                                        textTransform: "none",
                                                        fontSize: "12px",
                                                        color: THEME.primary,
                                                        fontWeight: 500,
                                                        "&:hover": {
                                                            backgroundColor: `${THEME.primary}10`
                                                        }
                                                    }}
                                                >
                                                    View More ({allEvidences.length - visibleCustomersCount} remaining)
                                                </Button>
                                            )}
                                            <Button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsCustomersExpanded(false);
                                                    setVisibleCustomersCount(10);
                                                }}
                                                sx={{
                                                    textTransform: "none",
                                                    fontSize: "12px",
                                                    color: THEME.textSecondary,
                                                    fontWeight: 500,
                                                    "&:hover": {
                                                        backgroundColor: `${THEME.primary}10`
                                                    }
                                                }}
                                            >
                                                Collapse
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </>
            )}
        </Drawer>
    );
};

// Sub-tabs for Track Requests
type ViewType = "submissions" | "dcr-requests" | "customers";

interface CTLTextFilterPillProps {
    name: string;
    placeholder?: string;
    onApply: (value: string) => void;
}

/**
 * CTLTextFilterPill - Text search filter pill using FilterPill for consistent look
 * Shows text input instead of checkboxes
 */
const CTLTextFilterPill: React.FC<CTLTextFilterPillProps> = ({ name, placeholder, onApply }) => {
    const [inputValue, setInputValue] = useState("");
    const [appliedValue, setAppliedValue] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);

    const handleApply = useCallback(() => {
        setAppliedValue(inputValue);
        onApply(inputValue);
        containerRef.current?.click(); // Close the menu
    }, [inputValue, onApply]);

    const handleCancel = useCallback(() => {
        setInputValue(appliedValue);
        containerRef.current?.click(); // Close the menu
    }, [appliedValue]);

    const handleReset = useCallback(() => {
        setInputValue("");
        setAppliedValue("");
        onApply("");
    }, [onApply]);

    const renderMenuList: IRenderFunction<IContextualMenuListProps> = useCallback(
        () => (
            <>
                <div style={{ borderBottom: "1px solid #ccc", padding: 10 }}>
                    <label style={{ display: "block", marginBottom: 5, fontSize: 14 }}>{name} contains</label>
                    <SearchBox
                        placeholder={placeholder || "Search..."}
                        value={inputValue}
                        onChange={(_, newValue) => setInputValue(newValue || "")}
                        onKeyDown={(e) => e.key === "Enter" && handleApply()}
                        autoFocus
                        styles={{ root: { width: 200 } }}
                    />
                </div>
                <Stack
                    horizontal
                    tokens={{ childrenGap: 10 }}
                    styles={{ root: { borderTop: "1px solid #ccc", padding: 10 } }}
                >
                    <PrimaryButton text="Apply" onClick={handleApply} />
                    <DefaultButton text="Cancel" onClick={handleCancel} />
                </Stack>
            </>
        ),
        [name, placeholder, inputValue, handleApply, handleCancel]
    );

    const displayValue = appliedValue ? [appliedValue] : undefined;

    return (
        <div ref={containerRef}>
            <FilterPill
                name={name}
                defaultValue="All"
                value={displayValue}
                isRemovable={true}
                styles={FILTER_PILL_STYLES}
                menuProps={{
                    items: [{ key: "input", text: "" }], // Dummy item, we override rendering
                    onRenderMenuList: renderMenuList
                }}
                resetProps={{
                    onClick: handleReset,
                    "aria-label": `Clear ${name} filter`
                }}
            />
        </div>
    );
};

/**
 * Status Chip Component - Shows status with color coding
 * Uses STATUS_CONFIG for known statuses, dynamic color for unknown ones
 */
export const StatusChip: React.FC<{ status: string }> = ({ status }) => {
    // Find exact match first, then partial match
    const exactMatch = STATUS_CONFIG[status];
    const partialMatch = !exactMatch ? Object.keys(STATUS_CONFIG).find((key) =>
        status.toLowerCase().includes(key.toLowerCase())
    ) : null;

    // Get config or generate dynamic color for unknown status
    let config;
    if (exactMatch) {
        config = exactMatch;
    } else if (partialMatch) {
        config = STATUS_CONFIG[partialMatch];
    } else {
        // Generate consistent color based on status string hash
        const hash = status.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const dynamicColor = DYNAMIC_STATUS_COLORS[hash % DYNAMIC_STATUS_COLORS.length];
        config = {
            label: status.length > 20 ? status.substring(0, 18) + "..." : status,
            ...dynamicColor
        };
    }

    return (
        <Chip
            label={config.label}
            size="small"
            title={status}
            sx={{
                backgroundColor: config.bgColor,
                color: config.color,
                fontWeight: 500,
                fontSize: "12px",
                height: "24px"
            }}
        />
    );
};

/**
 * Priority Badge Component
 */
export const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
    const config = PRIORITY_CONFIG[priority] || { label: priority, color: THEME.textSecondary };

    if (!priority || priority === "Not Available") {
        return <Box component="span" sx={{ fontSize: "12px", color: THEME.textSecondary }}>—</Box>;
    }

    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px"
            }}
        >
            <Box
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: config.color
                }}
            />
            <Box component="span" sx={{ fontSize: "12px", fontWeight: 500 }}>
                {config.label}
            </Box>
        </Box>
    );
};

/**
 * Stage Chip Component
 */
const StageChip: React.FC<{ stage: string }> = ({ stage }) => {
    if (!stage) return <Box component="span" sx={{ fontSize: "12px", color: THEME.textSecondary }}>—</Box>;

    const config = STAGE_CONFIG[stage] || {
        label: stage,
        color: THEME.textSecondary,
        bgColor: "#F0F0F0"
    };

    return (
        <Chip
            label={config.label}
            size="small"
            sx={{
                backgroundColor: config.bgColor,
                color: config.color,
                fontWeight: 500,
                fontSize: "11px",
                height: "22px"
            }}
        />
    );
};

// Use shared CustomDataGridToolbar component

/**
 * Renders a comma-separated list of identifiers in a compact grid cell.
 * Shows the full list on hover so multi-case feature requests stay readable.
 */
const MultiValueCell: React.FC<{ value?: string }> = ({ value }) => {
    const text = (value || "").trim();
    if (!text) {
        return <Box component="span" sx={{ fontSize: "12px", color: THEME.textSecondary }}>—</Box>;
    }
    return (
        <Tooltip title={text}>
            <Box
                component="span"
                sx={{
                    fontSize: "12px",
                    color: THEME.textPrimary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                }}
            >
                {text}
            </Box>
        </Tooltip>
    );
};

/**
 * Shared traceability columns (DCR / CSS / Case Number) used by both the
 * Track Requests and Review & Analyze grids.
 */
const isLinkableUrl = (url: string | null | undefined): boolean => {
    if (!url || url.trim() === "" || url === "#") return false;
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
};

const buildTraceabilityColumns = (): GridColDef[] => [
    {
        field: "dcrRequestId",
        headerName: "DCR ID",
        width: 120,
        renderCell: (params: GridRenderCellParams) => {
            const row = params.row as IFeatureRequestSummary;
            if (!row.dcrRequestId) {
                return <Box component="span" sx={{ fontSize: "12px", color: THEME.textSecondary }}>—</Box>;
            }
            return isLinkableUrl(row.dcrRequestURL) ? (
                <Box
                    component="a"
                    href={row.dcrRequestURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    sx={{
                        fontSize: "12px",
                        color: THEME.primary,
                        textDecoration: "none",
                        fontWeight: 500,
                        "&:hover": { textDecoration: "underline" }
                    }}
                >
                    {row.dcrRequestId}
                </Box>
            ) : (
                <Box component="span" sx={{ fontSize: "12px", color: THEME.textPrimary }}>{row.dcrRequestId}</Box>
            );
        }
    },
    {
        field: "cssIds",
        headerName: "CSS Number",
        width: 140,
        renderCell: (params: GridRenderCellParams) => <MultiValueCell value={params.value as string} />
    },
    {
        field: "caseNumbers",
        headerName: "Case Number",
        width: 140,
        renderCell: (params: GridRenderCellParams) => <MultiValueCell value={params.value as string} />
    }
];

/**
 * All Submissions View Component - Server-side pagination with grouped feature requests
 */
// Maps DataGrid column fields to the server's *Contains filter params (server-side column filtering).
const COLUMN_FILTER_FIELD_MAP: Record<string, string> = {
    featureRequestId: "featureRequestIdContains",
    title: "titleContains",
    productName: "productContains",
    customerDisplay: "customerContains",
    caseNumbers: "caseNumberContains"
};
const AllSubmissionsView: React.FC<{
    filters: IFeatureEvidenceFilters;
    appliedFilters: Record<string, string[]>;
    onFilterChange: (filterKey: string, values: string[]) => void;
    onDateRangeFilterChange: (fromKey: string, toKey: string, fromValue: string, toValue: string) => void;
    onOpenDetails: (featureRequestIdWithSource: string) => void;
    extraTopOffset?: number;
}> = ({ filters, appliedFilters, onFilterChange, onDateRangeFilterChange, onOpenDetails, extraTopOffset = 0 }) => {
    // Responsive grid sizing
    const { gridHeight, defaultPageSize } = useResponsiveGrid(420 + extraTopOffset);

    // Pagination state - use responsive default page size
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [sortModel, setSortModel] = useState<{ field: string; sort: "asc" | "desc" }>({ field: "lastChangedDate", sort: "desc" });

    // Built-in column filter mapped to server-side *Contains params (whole dataset, not just the page).
    const [columnFilters, setColumnFilters] = useState<Partial<IPaginatedEvidenceRequest>>({});
    const handleColumnFilterChange = useCallback((model: GridFilterModel) => {
        const next: Partial<IPaginatedEvidenceRequest> = {};
        model.items.forEach((item) => {
            const key = COLUMN_FILTER_FIELD_MAP[item.field];
            const value = typeof item.value === "string" ? item.value.trim() : "";
            if (key && value) {
                (next as Record<string, string>)[key] = value;
            }
        });
        setColumnFilters(next);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, []);

    // Update page size when responsive size changes (only on initial render or significant change)
    useEffect(() => {
        setPaginationModel(prev => {
            // Only update if not manually changed by user
            if (prev.pageSize === 25 || prev.pageSize === 50 || prev.pageSize === 100) {
                return { ...prev, pageSize: defaultPageSize };
            }
            return prev;
        });
    }, [defaultPageSize]);

    // Search state
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Export state
    const [isExporting, setIsExporting] = useState(false);
    const [triggerExportQuery] = useLazyGetPaginatedFeatureRequestsQuery();

    // Prefetch panel details for visible items
    const [prefetchPanels] = usePrefetchPanelsMutation();
    const prefetchedRef = useRef<Set<string>>(new Set());
    const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Handle row hover - prefetch panel data after 300ms hover
    const handleRowHover = useCallback((id: string) => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
        }
        if (id && !prefetchedRef.current.has(id)) {
            hoverTimerRef.current = setTimeout(() => {
                prefetchedRef.current.add(id);
                prefetchPanels([id]);
            }, 300);
        }
    }, [prefetchPanels]);

    const handleRowHoverEnd = useCallback(() => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
    }, []);

    // Debounce search input - 500ms delay, minimum 3 characters
    useEffect(() => {
        const timer = setTimeout(() => {
            // Only search if empty (clear) or at least 3 characters
            if (searchText === "" || searchText.length >= 3) {
                setDebouncedSearch(searchText);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchText]);

    // Build request params from filters
    const requestParams: IPaginatedEvidenceRequest = useMemo(() => {
        const params: IPaginatedEvidenceRequest = {
            page: paginationModel.page + 1, // API uses 1-indexed pages
            pageSize: paginationModel.pageSize,
            sortBy: sortModel.field,
            sortDirection: sortModel.sort,
            searchTerm: debouncedSearch || undefined
        };

        // Map applied filters to request params
        Object.entries(appliedFilters).forEach(([key, values]) => {
            if (!values || values.length === 0) return;

            switch (key) {
                case "featureRequestIdContains":
                    params.featureRequestIdContains = values[0];
                    break;
                case "featureEvidenceIdContains":
                    params.featureEvidenceIdContains = values[0];
                    break;
                case "productContains":
                    params.productContains = values[0];
                    break;
                case "product":
                    params.products = values.join(",");
                    break;
                case "titleContains":
                    params.titleContains = values[0];
                    break;
                case "customerContains":
                    params.customerContains = values[0];
                    break;
                case "tenantNameContains":
                    params.tenantNameContains = values[0];
                    break;
                case "tenantIdContains":
                    params.tenantIdContains = values[0];
                    break;
                case "submitterContains":
                    params.submitterContains = values[0];
                    break;
                case "submitter":
                    params.submitters = values.join(",");
                    break;
                case "tagContains":
                    params.tagContains = values[0];
                    break;
                case "caseNumberContains":
                    params.caseNumberContains = values[0];
                    break;
                case "caseNumber":
                    params.caseNumbers = values.join(",");
                    break;
                case "status":
                    params.statuses = values.join(",");
                    break;
                case "priority":
                    params.priorities = values.join(",");
                    break;
                case "stage":
                    params.stages = values.join(",");
                    break;
                case "eta":
                    params.etas = values.join(",");
                    break;
                case "industry":
                    params.industries = values.join(",");
                    break;
                case "vertical":
                    params.verticals = values.join(",");
                    break;
                case "region":
                    params.regions = values.join(",");
                    break;
                case "source":
                    params.sources = values.join(",");
                    break;
                case "featureCategory":
                    params.featureCategories = values.join(",");
                    break;
                case "createdDateFrom":
                    params.createdDateFrom = values[0];
                    break;
                case "createdDateTo":
                    params.createdDateTo = values[0];
                    break;
                case "lastChangedDateFrom":
                    params.lastChangedDateFrom = values[0];
                    break;
                case "lastChangedDateTo":
                    params.lastChangedDateTo = values[0];
                    break;
            }
        });

        return { ...params, ...columnFilters };
    }, [paginationModel, sortModel, appliedFilters, debouncedSearch, columnFilters]);

    // Fetch paginated grouped feature requests
    // refetchOnMountOrArgChange: 60 means only refetch if data is older than 60 seconds
    const { data: paginatedData, isLoading, isFetching } = useGetPaginatedFeatureRequestsQuery(requestParams, {
        refetchOnMountOrArgChange: 60
    });

    // Prefetch panel details for visible items (background, non-blocking)
    useEffect(() => {
        if (paginatedData?.featureRequests && !isLoading && !isFetching) {
            // Get IDs of visible items that haven't been prefetched yet
            const newIds = paginatedData.featureRequests
                .map(r => r.featureRequestIdWithSource)
                .filter(id => id && !prefetchedRef.current.has(id))
                .slice(0, 10); // Limit to first 10 visible items

            if (newIds.length > 0) {
                // Mark as prefetching to avoid duplicate requests
                newIds.forEach(id => prefetchedRef.current.add(id));
                // Fire prefetch in background (don't await)
                prefetchPanels(newIds);
            }
        }
    }, [paginatedData?.featureRequests, isLoading, isFetching, prefetchPanels]);

    // Reset to first page when filters or search change
    useEffect(() => {
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, [appliedFilters, debouncedSearch]);

    // Export all filtered data handler
    const handleExportAll = useCallback(async () => {
        setIsExporting(true);
        try {
            // Build export params - same filters but fetch ALL records
            const exportParams: IPaginatedEvidenceRequest = {
                page: 1,
                pageSize: 1000000, // Large value to get all records
                sortBy: sortModel.field,
                sortDirection: sortModel.sort,
                searchTerm: debouncedSearch || undefined
            };

            // Copy all filter params from requestParams (except pagination)
            Object.entries(appliedFilters).forEach(([key, values]) => {
                if (!values || values.length === 0) return;
                switch (key) {
                    case "product": exportParams.products = values.join(","); break;
                    case "submitter": exportParams.submitters = values.join(","); break;
                    case "status": exportParams.statuses = values.join(","); break;
                    case "priority": exportParams.priorities = values.join(","); break;
                    case "industry": exportParams.industries = values.join(","); break;
                    case "vertical": exportParams.verticals = values.join(","); break;
                    case "region": exportParams.regions = values.join(","); break;
                    case "source": exportParams.sources = values.join(","); break;
                    case "featureCategory": exportParams.featureCategories = values.join(","); break;
                    case "caseNumber": exportParams.caseNumbers = values.join(","); break;
                    case "caseNumberContains": exportParams.caseNumberContains = values[0]; break;
                    case "createdDateFrom": exportParams.createdDateFrom = values[0]; break;
                    case "createdDateTo": exportParams.createdDateTo = values[0]; break;
                    case "lastChangedDateFrom": exportParams.lastChangedDateFrom = values[0]; break;
                    case "lastChangedDateTo": exportParams.lastChangedDateTo = values[0]; break;
                }
            });

            const result = await triggerExportQuery(exportParams).unwrap();

            if (result?.featureRequests && result.featureRequests.length > 0) {
                // Convert to CSV
                const headers = [
                    "Feature Request ID",
                    "Title",
                    "Product",
                    "Status",
                    "Priority",
                    "Source",
                    "Customer",
                    "Evidence Count",
                    "Customer Count",
                    "Created Date",
                    "Last Changed Date",
                    "Engineering PM",
                    "Customer Facing Status",
                    "Feature Category",
                    "DCR ID",
                    "CSS Number",
                    "Case Number"
                ];

                const csvRows = [headers.join(",")];

                result.featureRequests.forEach(row => {
                    const values = [
                        `"${(row.featureRequestId || "").replace(/"/g, '""')}"`,
                        `"${(row.title || "").replace(/"/g, '""')}"`,
                        `"${(row.productName || "").replace(/"/g, '""')}"`,
                        `"${(row.status || "").replace(/"/g, '""')}"`,
                        `"${(row.priority || "").replace(/"/g, '""')}"`,
                        `"${(row.source || "").replace(/"/g, '""')}"`,
                        `"${(row.customerDisplay || "").replace(/"/g, '""')}"`,
                        row.evidenceCount || 0,
                        row.customerCount || 0,
                        `"${(row.latestCreatedDate || "").replace(/"/g, '""')}"`,
                        `"${(row.lastChangedDate || "").replace(/"/g, '""')}"`,
                        `"${(row.engineeringPM || "").replace(/"/g, '""')}"`,
                        `"${(row.customerFacingStatus || "").replace(/"/g, '""')}"`,
                        `"${(row.featureCategory || "").replace(/"/g, '""')}"`,
                        `"${(row.dcrRequestId || "").replace(/"/g, '""')}"`,
                        `"${(row.cssIds || "").replace(/"/g, '""')}"`,
                        `"${(row.caseNumbers || "").replace(/"/g, '""')}"`
                    ];
                    csvRows.push(values.join(","));
                });

                const csvContent = csvRows.join("\n");
                const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `feature-requests-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setIsExporting(false);
        }
    }, [appliedFilters, debouncedSearch, sortModel, triggerExportQuery]);

    // Helper function to extract stage from status
    const extractStage = (status: string): string => {
        if (status?.toUpperCase().includes("GENERALLY AVAILABLE") || status?.toUpperCase() === "GA") return "GA";
        if (status?.includes("Public Preview")) return "Public Preview";
        if (status?.includes("Private Preview")) return "Private Preview";
        return "";
    };

    const formatDate = formatDisplayDate;

    const columns: GridColDef[] = [
        {
            field: "featureRequestId",
            headerName: "Feature Request ID",
            width: 160,
            renderCell: (params: GridRenderCellParams) => (
                <Box
                    component="span"
                    sx={{
                        color: THEME.primary,
                        fontWeight: 500,
                        fontSize: "12px",
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" }
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenDetails((params.row as IFeatureRequestSummary).featureRequestIdWithSource);
                    }}
                >
                    {params.value}
                </Box>
            )
        },
        {
            field: "title",
            headerName: "Title",
            flex: 1,
            minWidth: 250,
            renderCell: (params: GridRenderCellParams) => (
                <Tooltip title={params.value || ""}>
                    <Box
                        component="span"
                        sx={{
                            fontSize: "12px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                        }}
                    >
                        {params.value}
                    </Box>
                </Tooltip>
            )
        },
        {
            field: "productName",
            headerName: "Product",
            width: 130,
            renderCell: (params: GridRenderCellParams) => (
                <Box component="span" sx={{ fontSize: "12px" }}>
                    {params.value || "—"}
                </Box>
            )
        },
        {
            field: "customerCount",
            headerName: "No of Customers",
            width: 120,
            align: "center",
            headerAlign: "center",
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value || 0}
                    size="small"
                    sx={{
                        backgroundColor: params.value > 1 ? "#E8F5E9" : "#F5F5F5",
                        color: params.value > 1 ? THEME.success : THEME.textSecondary,
                        fontWeight: 600,
                        fontSize: "11px",
                        height: "22px",
                        minWidth: "36px"
                    }}
                />
            )
        },
        {
            field: "status",
            headerName: "Status",
            width: 150,
            renderCell: (params: GridRenderCellParams) => <StatusChip status={params.value || ""} />
        },
        {
            field: "customerFacingStatus",
            headerName: "Customer Facing Status",
            width: 180,
            renderCell: (params: GridRenderCellParams) => (
                <Tooltip title={params.value || ""}>
                    <Box
                        component="span"
                        sx={{
                            fontSize: "12px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: params.value ? THEME.textPrimary : THEME.textSecondary
                        }}
                    >
                        {params.value || "—"}
                    </Box>
                </Tooltip>
            )
        },
        {
            field: "priority",
            headerName: "Priority",
            width: 80,
            renderCell: (params: GridRenderCellParams) => <PriorityBadge priority={params.value} />
        },
        {
            field: "source",
            headerName: "Source",
            width: 80,
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value || "—"}
                    size="small"
                    sx={{
                        backgroundColor: params.value === "CRM" ? "#E5F1FB" :
                            params.value === "UAT" ? "#E8F5E9" :
                                params.value === "CSS" ? "#FFF3E0" : "#F5F5F5",
                        color: params.value === "CRM" ? THEME.primary :
                            params.value === "UAT" ? THEME.success :
                                params.value === "CSS" ? "#E65100" : THEME.textSecondary,
                        fontWeight: 500,
                        fontSize: "11px",
                        height: "22px"
                    }}
                />
            )
        },
        {
            field: "latestCreatedDate",
            headerName: "Created",
            width: 120,
            valueGetter: (value, row) => formatDate(row.latestCreatedDate),
            renderCell: (params: GridRenderCellParams) => (
                <Box
                    component="span"
                    sx={{
                        fontSize: "12px",
                        color: params.value && params.value !== "—" ? THEME.textPrimary : THEME.textSecondary
                    }}
                >
                    {params.value || "—"}
                </Box>
            )
        },
        {
            field: "lastChangedDate",
            headerName: "Last Changed",
            width: 120,
            valueGetter: (value, row) => formatDate(row.lastChangedDate),
            renderCell: (params: GridRenderCellParams) => (
                <Box
                    component="span"
                    sx={{
                        fontSize: "12px",
                        color: params.value && params.value !== "—" ? THEME.textPrimary : THEME.textSecondary
                    }}
                >
                    {params.value || "—"}
                </Box>
            )
        },
        ...buildTraceabilityColumns()
    ];

    const hasActiveFilters = Object.values(appliedFilters).some((v) => v.length > 0);

    // Initial loading state - show centered loader before grid
    if (isLoading && !paginatedData) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 400,
                    gap: 2
                }}
            >
                <CircularProgress size={40} sx={{ color: THEME.primary }} />
                <Typography sx={{ color: THEME.textSecondary, fontSize: "14px" }}>
                    Loading feature requests...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}>
            {/* Rich Search Bar */}
            <RichSearchBox
                value={searchText}
                onChange={setSearchText}
                onClear={() => setSearchText("")}
                placeholder="Search by Feature Request ID, Tenant Name, Tenant ID, Customer Name, Title, or Tags"
                showHint={searchText.length > 0 && searchText.length < 3}
            />

            {/* Filters Row - Single row */}
            <div
                className={mergeStyles({
                    margin: "-4px",
                    display: "flex",
                    flexWrap: "wrap",
                    paddingBottom: 16,
                    alignItems: "center"
                })}
            >
                <CTLFilterPill
                    name="Product"
                    options={filters.products}
                    selectedValues={appliedFilters.product}
                    onApply={(values) => onFilterChange("product", values || [])}
                    urlParamKey="product"
                />
                <CTLFilterPill
                    name="Submitter"
                    options={filters.submitters}
                    selectedValues={appliedFilters.submitter}
                    onApply={(values) => onFilterChange("submitter", values || [])}
                    urlParamKey="submitter"
                />
                <CTLFilterPill
                    name="Status"
                    options={filters.statuses}
                    selectedValues={appliedFilters.status}
                    onApply={(values) => onFilterChange("status", values || [])}
                    urlParamKey="status"
                />
                <CTLFilterPill
                    name="Priority"
                    options={filters.priorities}
                    selectedValues={appliedFilters.priority}
                    onApply={(values) => onFilterChange("priority", values || [])}
                    urlParamKey="priority"
                />
                <CTLFilterPill
                    name="Industry"
                    options={filters.industries}
                    selectedValues={appliedFilters.industry}
                    onApply={(values) => onFilterChange("industry", values || [])}
                    urlParamKey="industry"
                />
                <CTLFilterPill
                    name="Vertical"
                    options={filters.verticals}
                    selectedValues={appliedFilters.vertical}
                    onApply={(values) => onFilterChange("vertical", values || [])}
                    urlParamKey="vertical"
                />
                <CTLFilterPill
                    name="Region"
                    options={filters.regions}
                    selectedValues={appliedFilters.region}
                    onApply={(values) => onFilterChange("region", values || [])}
                    urlParamKey="region"
                />
                <CTLFilterPill
                    name="Source"
                    options={filters.sources}
                    selectedValues={appliedFilters.source}
                    onApply={(values) => onFilterChange("source", values || [])}
                    urlParamKey="source"
                />
                <CTLSearchableFilterPill
                    name="Feature Category"
                    options={filters.featureCategories || []}
                    selectedValues={appliedFilters.featureCategory}
                    onApply={(values) => onFilterChange("featureCategory", values || [])}
                    maxVisibleItems={50}
                />
                <CTLSearchableFilterPill
                    name="Case Number"
                    options={filters.caseNumbers || []}
                    selectedValues={appliedFilters.caseNumber}
                    onApply={(values) => onFilterChange("caseNumber", values || [])}
                    maxVisibleItems={50}
                />
                <CTLDateRangeFilterPill
                    name="Created Date"
                    onApply={(from, to) => {
                        onDateRangeFilterChange("createdDateFrom", "createdDateTo", from, to);
                    }}
                />
                <CTLDateRangeFilterPill
                    name="Last Updated Date"
                    onApply={(from, to) => {
                        onDateRangeFilterChange("lastChangedDateFrom", "lastChangedDateTo", from, to);
                    }}
                />
                <ShareFilterButton />
            </div>

            {/* Data Grid - Server-side pagination */}
            <Paper
                elevation={0}
                sx={{
                    border: `1px solid ${THEME.border}`,
                    borderRadius: "8px",
                    overflow: "hidden",
                    flex: 1,
                    minHeight: 200,
                    maxHeight: "calc(100vh - 380px)",
                    display: "flex",
                    flexDirection: "column",
                    "& .MuiDataGrid-root": {
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        border: "none"
                    },
                    "& .MuiDataGrid-main": {
                        flex: 1,
                        overflow: "hidden",
                        minHeight: 0
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        overflow: "auto"
                    },
                    "& .MuiDataGrid-footerContainer": {
                        minHeight: 52,
                        flexShrink: 0,
                        borderTop: `1px solid ${THEME.border}`
                    }
                }}
            >
                <DataGrid
                    rows={paginatedData?.featureRequests || []}
                    columns={columns}
                    loading={isLoading || isFetching}
                    density="compact"
                    getRowId={(row) => row.featureRequestIdWithSource}

                    // Server-side pagination
                    paginationMode="server"
                    rowCount={paginatedData?.totalCount || 0}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[25, 50, 100]}

                    // Server-side sorting
                    sortingMode="server"
                    sortModel={[{ field: sortModel.field, sort: sortModel.sort }]}
                    onSortModelChange={(model) => {
                        if (model.length > 0) {
                            setSortModel({ field: model[0].field, sort: model[0].sort || "asc" });
                        } else {
                            // When user clicks third time (unsorted), toggle back to opposite direction
                            setSortModel((prev) => ({ ...prev, sort: prev.sort === "asc" ? "desc" : "asc" }));
                        }
                    }}

                    disableRowSelectionOnClick

                    // Column filter runs server-side (maps to *Contains params) so it searches the whole
                    // dataset; the client-side quick filter stays off since it only sees the loaded page.
                    filterMode="server"
                    onFilterModelChange={handleColumnFilterChange}

                    // Prefetch panel data on row hover
                    slotProps={{
                        row: {
                            onMouseEnter: (event: React.MouseEvent<HTMLDivElement>) => {
                                const rowId = event.currentTarget.getAttribute("data-id");
                                if (rowId) handleRowHover(rowId);
                            },
                            onMouseLeave: handleRowHoverEnd
                        }
                    }}

                    slots={{
                        toolbar: () => (
                            <CustomDataGridToolbar
                                filterModel={{ items: [] }}
                                toolbarConfig={{ disableGridToolbarQuickFilter: true }}
                                exportCsvButtonProps={{
                                    buttonLabel: isExporting ? "Exporting..." : "Export All",
                                    fileNamePrefix: "feature-requests",
                                    tooltip: "Export all filtered data to CSV",
                                    onCustomExport: handleExportAll,
                                    isExporting: isExporting
                                }}
                            />
                        ),
                        loadingOverlay: () => (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                    width: "100%",
                                    backgroundColor: "rgba(255, 255, 255, 0.95)"
                                }}
                            >
                                <CircularProgress size={40} sx={{ color: THEME.primary, mb: 2 }} />
                                <Typography sx={{ color: THEME.textPrimary, fontSize: "14px", fontWeight: 500 }}>
                                    Loading data...
                                </Typography>
                            </Box>
                        ),
                        noRowsOverlay: () => (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                    py: 4,
                                    background: "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(250,251,252,1) 100%)"
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        maxWidth: 320
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: "20px",
                                            background: "linear-gradient(135deg, #F0F4FF 0%, #E8F0FE 100%)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mb: 3,
                                            boxShadow: "0 4px 12px rgba(0, 90, 158, 0.08)"
                                        }}
                                    >
                                        <Search20Regular style={{ color: THEME.primary, width: 36, height: 36, opacity: 0.8 }} />
                                    </Box>
                                    <Typography
                                        sx={{
                                            color: THEME.textPrimary,
                                            fontSize: "16px",
                                            fontWeight: 600,
                                            mb: 1
                                        }}
                                    >
                                        No Results Found
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: THEME.textSecondary,
                                            fontSize: "13px",
                                            textAlign: "center",
                                            lineHeight: 1.5
                                        }}
                                    >
                                        We couldn't find any feature requests matching your criteria. Try adjusting your filters or search terms.
                                    </Typography>
                                </Box>
                            </Box>
                        )
                    }}
                    sx={{
                        border: "none",
                        fontSize: "12px",
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: "#F8F9FA",
                            borderBottom: `1px solid ${THEME.border}`
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontSize: "12px",
                            fontWeight: 600
                        },
                        "& .MuiDataGrid-cell": {
                            borderBottom: `1px solid ${THEME.border}`,
                            fontSize: "12px"
                        },
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: `${THEME.primary}08`
                        },
                        "& .MuiDataGrid-menuIconButton": { color: "white" },
                        "& .MuiDataGrid-overlayWrapper": {
                            backgroundColor: "rgba(255, 255, 255, 0.98)"
                        }
                    }}
                />
            </Paper>
            {/* Explore Data Button Footer - Matches CustomCard footer style */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    minHeight: 45,
                    borderTop: `2px solid ${THEME.border}`,
                    backgroundColor: THEME.surface,
                    borderRadius: "0 0 8px 8px",
                    border: `1px solid ${THEME.border}`,
                    borderTopWidth: 0,
                    px: 1.5
                }}
            >
                <CTLExploreDataButton
                    query={CTL_FEATURE_EVIDENCES_QUERY}
                    chartTitle="All Submissions Table"
                />
            </Box>
        </Box>
    );
};

/**
 * Customer View Component
 */
const CustomerView: React.FC<{
    data: ICustomerSummary[];
    isLoading: boolean;
    onSelectCustomer: (customer: ICustomerSummary) => void;
    onSelectCustomerByTPID: (customer: ICustomerSummary) => void;
}> = ({ data, isLoading, onSelectCustomer, onSelectCustomerByTPID }) => {
    // Responsive grid sizing
    const { gridHeight, defaultPageSize } = useResponsiveGrid(380);

    // Search state with debounce
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Debounce search input - 500ms delay, minimum 3 characters
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchText === "" || searchText.length >= 3) {
                setDebouncedSearch(searchText);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchText]);

    // Filter data based on search
    const filteredData = useMemo(() => {
        if (!debouncedSearch) return data;

        const term = debouncedSearch.toLowerCase();
        return data.filter(
            (row) =>
                row.customerName?.toLowerCase().includes(term) ||
                row.tenantName?.toLowerCase().includes(term) ||
                row.tpid?.includes(term) ||
                row.tenantId?.toLowerCase().includes(term)
        );
    }, [data, debouncedSearch]);

    const columns: GridColDef[] = [
        {
            field: "tpid",
            headerName: "TPID",
            width: 120,
            renderCell: (params: GridRenderCellParams) => (
                params.value && params.row.tenantId ? (
                    <MuiLink
                        component="button"
                        onClick={() => onSelectCustomerByTPID(params.row as ICustomerSummary)}
                        sx={{
                            fontSize: "12px",
                            color: THEME.primary,
                            textDecoration: "none",
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" }
                        }}
                    >
                        {params.value}
                    </MuiLink>
                ) : (
                    <Box component="span" sx={{ fontSize: "12px", color: THEME.textSecondary }}>—</Box>
                )
            )
        },
        {
            field: "customerName",
            headerName: "Customer Name",
            width: 200,
            renderCell: (params: GridRenderCellParams) => (
                params.value && params.row.tenantId && params.row.tpid ? (
                    <MuiLink
                        component="button"
                        onClick={() => onSelectCustomerByTPID(params.row as ICustomerSummary)}
                        sx={{
                            fontSize: "12px",
                            fontWeight: 500,
                            color: THEME.primary,
                            textDecoration: "none",
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" }
                        }}
                    >
                        {params.value}
                    </MuiLink>
                ) : (
                    <Box component="span" sx={{ fontSize: "12px", fontWeight: 500 }}>
                        {params.value || "—"}
                    </Box>
                )
            )
        },
        {
            field: "tenantId",
            headerName: "Tenant ID",
            width: 250,
            renderCell: (params: GridRenderCellParams) => (
                params.value ? (
                    <MuiLink
                        component="button"
                        onClick={() => onSelectCustomer(params.row as ICustomerSummary)}
                        sx={{
                            fontSize: "12px",
                            color: THEME.primary,
                            textDecoration: "none",
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" }
                        }}
                    >
                        {params.value}
                    </MuiLink>
                ) : (
                    <Box component="span" sx={{ fontSize: "12px", color: THEME.textSecondary }}>—</Box>
                )
            )
        },
        {
            field: "tenantName",
            headerName: "Tenant Name",
            flex: 1,
            width: 150,
            renderCell: (params: GridRenderCellParams) => (
                params.row.tenantId ? (
                    <MuiLink
                        component="button"
                        onClick={() => onSelectCustomer(params.row as ICustomerSummary)}
                        sx={{
                            fontSize: "12px",
                            color: THEME.primary,
                            textDecoration: "none",
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" }
                        }}
                    >
                        {params.value || "—"}
                    </MuiLink>
                ) : (
                    <Box component="span" sx={{ fontSize: "12px", color: THEME.textSecondary }}>—</Box>
                )
            )
        },
        {
            field: "approvedCount",
            headerName: "Approved",
            width: 90,
            type: "number",
            renderCell: (params: GridRenderCellParams) => (
                <Box
                    component="span"
                    sx={{
                        fontSize: "12px",
                        color: params.value > 0 ? THEME.success : THEME.textSecondary,
                        fontWeight: params.value > 0 ? 600 : 400
                    }}
                >
                    {params.value}
                </Box>
            )
        },
        {
            field: "rejectedCount",
            headerName: "Rejected",
            width: 90,
            type: "number",
            renderCell: (params: GridRenderCellParams) => (
                <Box
                    component="span"
                    sx={{
                        fontSize: "12px",
                        color: params.value > 0 ? THEME.error : THEME.textSecondary,
                        fontWeight: params.value > 0 ? 600 : 400
                    }}
                >
                    {params.value}
                </Box>
            )
        },
        {
            field: "underConsiderationCount",
            headerName: "Under Consideration",
            width: 130,
            type: "number",
            renderCell: (params: GridRenderCellParams) => (
                <Box
                    component="span"
                    sx={{
                        fontSize: "12px",
                        color: params.value > 0 ? "#1976D2" : THEME.textSecondary,
                        fontWeight: params.value > 0 ? 600 : 400
                    }}
                >
                    {params.value}
                </Box>
            )
        },
        {
            field: "inProgressCount",
            headerName: "In Progress",
            width: 110,
            type: "number",
            description: "In Progress means this is in triage process",
            renderCell: (params: GridRenderCellParams) => (
                <Box
                    component="span"
                    sx={{
                        fontSize: "12px",
                        color: params.value > 0 ? THEME.warning : THEME.textSecondary,
                        fontWeight: params.value > 0 ? 600 : 400
                    }}
                >
                    {params.value}
                </Box>
            )
        }
    ];

    // Initial loading state - show centered loader before grid
    if (isLoading && data.length === 0) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 400,
                    gap: 2
                }}
            >
                <CircularProgress size={40} sx={{ color: THEME.primary }} />
                <Typography sx={{ color: THEME.textSecondary, fontSize: "14px" }}>
                    Loading customers...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}>
            {/* Rich Search Bar */}
            <RichSearchBox
                value={searchText}
                onChange={setSearchText}
                onClear={() => setSearchText("")}
                placeholder="Search by Tenant ID, Tenant Name, TPID, or Customer Name"
                showHint={searchText.length > 0 && searchText.length < 3}
            />

            {/* Data Grid */}
            <Paper
                elevation={0}
                sx={{
                    border: `1px solid ${THEME.border}`,
                    borderRadius: "8px",
                    overflow: "hidden",
                    flex: 1,
                    minHeight: 200,
                    maxHeight: "calc(100vh - 320px)",
                    display: "flex",
                    flexDirection: "column",
                    "& .MuiDataGrid-root": {
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        border: "none"
                    },
                    "& .MuiDataGrid-main": {
                        flex: 1,
                        overflow: "hidden",
                        minHeight: 0
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        overflow: "auto"
                    },
                    "& .MuiDataGrid-footerContainer": {
                        minHeight: 52,
                        flexShrink: 0,
                        borderTop: `1px solid ${THEME.border}`
                    }
                }}
            >
                <DataGrid
                    rows={filteredData}
                    columns={columns}
                    loading={isLoading}
                    density="compact"
                    getRowId={(row) => `${row.customerName}-${row.tpid}-${row.tenantId}`}
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: defaultPageSize } }
                    }}
                    disableRowSelectionOnClick
                    slots={{
                        toolbar: () => (
                            <CustomDataGridToolbar
                                filterModel={{ items: [] }}
                                toolbarConfig={{ disableGridToolbarQuickFilter: false }}
                                exportCsvButtonProps={{
                                    buttonLabel: "Export All",
                                    fileNamePrefix: "customer-summary",
                                    tooltip: "Export all filtered data to CSV"
                                }}
                            />
                        ),
                        loadingOverlay: () => (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                    width: "100%",
                                    backgroundColor: "rgba(255, 255, 255, 0.95)"
                                }}
                            >
                                <CircularProgress size={40} sx={{ color: THEME.primary, mb: 2 }} />
                                <Typography sx={{ color: THEME.textPrimary, fontSize: "14px", fontWeight: 500 }}>
                                    Loading data...
                                </Typography>
                            </Box>
                        ),
                        noRowsOverlay: () => (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                    py: 4,
                                    background: "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(250,251,252,1) 100%)"
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        maxWidth: 320
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: "20px",
                                            background: "linear-gradient(135deg, #F0F4FF 0%, #E8F0FE 100%)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mb: 3,
                                            boxShadow: "0 4px 12px rgba(0, 90, 158, 0.08)"
                                        }}
                                    >
                                        <People20Regular style={{ color: THEME.primary, width: 36, height: 36, opacity: 0.8 }} />
                                    </Box>
                                    <Typography
                                        sx={{
                                            color: THEME.textPrimary,
                                            fontSize: "16px",
                                            fontWeight: 600,
                                            mb: 1
                                        }}
                                    >
                                        No Customers Found
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: THEME.textSecondary,
                                            fontSize: "13px",
                                            textAlign: "center",
                                            lineHeight: 1.5
                                        }}
                                    >
                                        We couldn't find any customers matching your search. Try adjusting your search terms.
                                    </Typography>
                                </Box>
                            </Box>
                        )
                    }}
                    sx={{
                        border: "none",
                        fontSize: "12px",
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: "#F8F9FA",
                            borderBottom: `1px solid ${THEME.border}`
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontSize: "12px",
                            fontWeight: 600
                        },
                        "& .MuiDataGrid-cell": {
                            borderBottom: `1px solid ${THEME.border}`,
                            fontSize: "12px"
                        },
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: `${THEME.primary}08`
                        },
                        "& .MuiDataGrid-menuIconButton": { color: "white" },
                        "& .MuiDataGrid-overlayWrapper": {
                            backgroundColor: "rgba(255, 255, 255, 0.98)"
                        }
                    }}
                />
            </Paper>
            {/* Explore Data Button Footer - Matches CustomCard footer style */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    minHeight: 45,
                    borderTop: `2px solid ${THEME.border}`,
                    backgroundColor: THEME.surface,
                    borderRadius: "0 0 8px 8px",
                    border: `1px solid ${THEME.border}`,
                    borderTopWidth: 0,
                    px: 1.5
                }}
            >
                <CTLExploreDataButton
                    query={CTL_CUSTOMER_SUMMARY_QUERY}
                    chartTitle="Customer Summary Table"
                />
            </Box>
        </Box>
    );
};

/**
 * CurrentMonthCustomers - Shows count of unique customers who submitted evidence this month
 */
const CurrentMonthCustomers: React.FC<{ count: number | null | undefined }> = ({ count }) => {
    // Only show positive counts (0, null, undefined, or negative values show as dash)
    if (count === null || count === undefined || count <= 0) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                <Typography sx={{ fontSize: "12px", color: THEME.textSecondary }}>—</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
            <Tooltip title="Number of unique customers who submitted evidence for this feature in the current month" arrow>
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 1.5,
                        py: 0.25,
                        borderRadius: "12px",
                        backgroundColor: "#E3F2FD",
                        border: "1px solid #90CAF9",
                        minWidth: "32px",
                        cursor: "help"
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            lineHeight: 1,
                            color: "#1565C0"
                        }}
                    >
                        {count}
                    </Typography>
                </Box>
            </Tooltip>
        </Box>
    );
};

/**
 * All Requests View Component - For Review & Analyze tab
 * Similar to AllSubmissionsView but includes current month customers column
 */
const AllRequestsView: React.FC<{
    filters: IFeatureEvidenceFilters;
    appliedFilters: Record<string, string[]>;
    onFilterChange: (filterKey: string, values: string[]) => void;
    onDateRangeFilterChange: (fromKey: string, toKey: string, fromValue: string, toValue: string) => void;
    onOpenDetails: (featureRequestIdWithSource: string) => void;
}> = ({ filters, appliedFilters, onFilterChange, onDateRangeFilterChange, onOpenDetails }) => {
    // Responsive grid sizing - larger offset for Review & Analyze which has more UI above the grid
    const { gridHeight, defaultPageSize } = useResponsiveGrid(580);

    // Pagination state - use responsive default page size
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [sortModel, setSortModel] = useState<{ field: string; sort: "asc" | "desc" }>({ field: "lastChangedDate", sort: "desc" });

    // Built-in column filter mapped to server-side *Contains params (whole dataset, not just the page).
    const [columnFilters, setColumnFilters] = useState<Partial<IPaginatedEvidenceRequest>>({});
    const handleColumnFilterChange = useCallback((model: GridFilterModel) => {
        const next: Partial<IPaginatedEvidenceRequest> = {};
        model.items.forEach((item) => {
            const key = COLUMN_FILTER_FIELD_MAP[item.field];
            const value = typeof item.value === "string" ? item.value.trim() : "";
            if (key && value) {
                (next as Record<string, string>)[key] = value;
            }
        });
        setColumnFilters(next);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, []);

    // Update page size when responsive size changes
    useEffect(() => {
        setPaginationModel(prev => {
            if (prev.pageSize === 25 || prev.pageSize === 50 || prev.pageSize === 100) {
                return { ...prev, pageSize: defaultPageSize };
            }
            return prev;
        });
    }, [defaultPageSize]);

    // Search state
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Export state
    const [isExporting, setIsExporting] = useState(false);
    const [triggerExportQuery] = useLazyGetPaginatedFeatureRequestsQuery();

    // Prefetch panel details on hover
    const [prefetchPanels] = usePrefetchPanelsMutation();
    const prefetchedRef = useRef<Set<string>>(new Set());
    const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Handle row hover - prefetch panel data after 300ms hover
    const handleRowHover = useCallback((id: string) => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
        }
        if (id && !prefetchedRef.current.has(id)) {
            hoverTimerRef.current = setTimeout(() => {
                prefetchedRef.current.add(id);
                prefetchPanels([id]);
            }, 300);
        }
    }, [prefetchPanels]);

    const handleRowHoverEnd = useCallback(() => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
    }, []);

    // Debounce search input - 500ms delay, minimum 3 characters
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchText === "" || searchText.length >= 3) {
                setDebouncedSearch(searchText);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchText]);

    // Build request params from filters
    const requestParams: IPaginatedEvidenceRequest = useMemo(() => {
        const params: IPaginatedEvidenceRequest = {
            page: paginationModel.page + 1,
            pageSize: paginationModel.pageSize,
            sortBy: sortModel.field,
            sortDirection: sortModel.sort,
            searchTerm: debouncedSearch || undefined
        };

        // Map applied filters to request params
        Object.entries(appliedFilters).forEach(([key, values]) => {
            if (!values || values.length === 0) return;

            switch (key) {
                case "product":
                    params.products = values.join(",");
                    break;
                case "submitter":
                    params.submitters = values.join(",");
                    break;
                case "status":
                    params.statuses = values.join(",");
                    break;
                case "priority":
                    params.priorities = values.join(",");
                    break;
                case "stage":
                    params.stages = values.join(",");
                    break;
                case "eta":
                    params.etas = values.join(",");
                    break;
                case "industry":
                    params.industries = values.join(",");
                    break;
                case "vertical":
                    params.verticals = values.join(",");
                    break;
                case "region":
                    params.regions = values.join(",");
                    break;
                case "source":
                    params.sources = values.join(",");
                    break;
                case "featureCategory":
                    params.featureCategories = values.join(",");
                    break;
                case "caseNumber":
                    params.caseNumbers = values.join(",");
                    break;
                case "caseNumberContains":
                    params.caseNumberContains = values[0];
                    break;
                case "createdDateFrom":
                    params.createdDateFrom = values[0];
                    break;
                case "createdDateTo":
                    params.createdDateTo = values[0];
                    break;
                case "lastChangedDateFrom":
                    params.lastChangedDateFrom = values[0];
                    break;
                case "lastChangedDateTo":
                    params.lastChangedDateTo = values[0];
                    break;
            }
        });

        return { ...params, ...columnFilters };
    }, [paginationModel, sortModel, appliedFilters, debouncedSearch, columnFilters]);

    // Fetch paginated grouped feature requests
    // refetchOnMountOrArgChange: 60 means only refetch if data is older than 60 seconds
    const { data: paginatedData, isLoading, isFetching } = useGetPaginatedFeatureRequestsQuery(requestParams, {
        refetchOnMountOrArgChange: 60
    });

    // Reset to first page when filters or search change
    useEffect(() => {
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, [appliedFilters, debouncedSearch]);

    // Export all filtered data handler
    const handleExportAll = useCallback(async () => {
        setIsExporting(true);
        try {
            // Build export params - same filters but fetch ALL records
            const exportParams: IPaginatedEvidenceRequest = {
                page: 1,
                pageSize: 1000000, // Large value to get all records
                sortBy: sortModel.field,
                sortDirection: sortModel.sort,
                searchTerm: debouncedSearch || undefined
            };

            // Copy all filter params
            Object.entries(appliedFilters).forEach(([key, values]) => {
                if (!values || values.length === 0) return;
                switch (key) {
                    case "product": exportParams.products = values.join(","); break;
                    case "submitter": exportParams.submitters = values.join(","); break;
                    case "status": exportParams.statuses = values.join(","); break;
                    case "priority": exportParams.priorities = values.join(","); break;
                    case "stage": exportParams.stages = values.join(","); break;
                    case "eta": exportParams.etas = values.join(","); break;
                    case "industry": exportParams.industries = values.join(","); break;
                    case "vertical": exportParams.verticals = values.join(","); break;
                    case "region": exportParams.regions = values.join(","); break;
                    case "source": exportParams.sources = values.join(","); break;
                    case "featureCategory": exportParams.featureCategories = values.join(","); break;
                    case "caseNumber": exportParams.caseNumbers = values.join(","); break;
                    case "caseNumberContains": exportParams.caseNumberContains = values[0]; break;
                    case "createdDateFrom": exportParams.createdDateFrom = values[0]; break;
                    case "createdDateTo": exportParams.createdDateTo = values[0]; break;
                    case "lastChangedDateFrom": exportParams.lastChangedDateFrom = values[0]; break;
                    case "lastChangedDateTo": exportParams.lastChangedDateTo = values[0]; break;
                }
            });

            const result = await triggerExportQuery(exportParams).unwrap();

            if (result?.featureRequests && result.featureRequests.length > 0) {
                const headers = [
                    "Feature Request ID",
                    "Title",
                    "Product",
                    "Status",
                    "Priority",
                    "Source",
                    "Customer",
                    "Evidence Count",
                    "Customer Count",
                    "Created Date",
                    "Last Changed Date",
                    "Engineering PM",
                    "Customer Facing Status",
                    "Feature Category",
                    "DCR ID",
                    "CSS Number",
                    "Case Number"
                ];

                const csvRows = [headers.join(",")];

                result.featureRequests.forEach(row => {
                    const values = [
                        `"${(row.featureRequestId || "").replace(/"/g, '""')}"`,
                        `"${(row.title || "").replace(/"/g, '""')}"`,
                        `"${(row.productName || "").replace(/"/g, '""')}"`,
                        `"${(row.status || "").replace(/"/g, '""')}"`,
                        `"${(row.priority || "").replace(/"/g, '""')}"`,
                        `"${(row.source || "").replace(/"/g, '""')}"`,
                        `"${(row.customerDisplay || "").replace(/"/g, '""')}"`,
                        row.evidenceCount || 0,
                        row.customerCount || 0,
                        `"${(row.latestCreatedDate || "").replace(/"/g, '""')}"`,
                        `"${(row.lastChangedDate || "").replace(/"/g, '""')}"`,
                        `"${(row.engineeringPM || "").replace(/"/g, '""')}"`,
                        `"${(row.customerFacingStatus || "").replace(/"/g, '""')}"`,
                        `"${(row.featureCategory || "").replace(/"/g, '""')}"`,
                        `"${(row.dcrRequestId || "").replace(/"/g, '""')}"`,
                        `"${(row.cssIds || "").replace(/"/g, '""')}"`,
                        `"${(row.caseNumbers || "").replace(/"/g, '""')}"`
                    ];
                    csvRows.push(values.join(","));
                });

                const csvContent = csvRows.join("\n");
                const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `all-requests-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setIsExporting(false);
        }
    }, [appliedFilters, debouncedSearch, sortModel, triggerExportQuery]);

    // Helper to format date - handles dd-MM-yyyy format from backend
    const formatDate = formatDisplayDate;

    const columns: GridColDef[] = [
        {
            field: "featureRequestId",
            headerName: "Feature Request ID",
            width: 150,
            renderCell: (params: GridRenderCellParams) => (
                <Box
                    component="span"
                    sx={{
                        color: THEME.primary,
                        fontWeight: 500,
                        fontSize: "12px",
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" }
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenDetails((params.row as IFeatureRequestSummary).featureRequestIdWithSource);
                    }}
                >
                    {params.value}
                </Box>
            )
        },
        {
            field: "title",
            headerName: "Title",
            flex: 1,
            minWidth: 150,
            renderCell: (params: GridRenderCellParams) => (
                <Tooltip title={params.value || ""}>
                    <Box
                        component="span"
                        sx={{
                            fontSize: "12px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                        }}
                    >
                        {params.value}
                    </Box>
                </Tooltip>
            )
        },
        {
            field: "productName",
            headerName: "Product",
            width: 110,
            renderCell: (params: GridRenderCellParams) => (
                <Box component="span" sx={{ fontSize: "12px" }}>
                    {params.value || "—"}
                </Box>
            )
        },
        {
            field: "customerCount",
            headerName: "Customers",
            width: 90,
            align: "center",
            headerAlign: "center",
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value || 0}
                    size="small"
                    sx={{
                        backgroundColor: params.value > 1 ? "#E8F5E9" : "#F5F5F5",
                        color: params.value > 1 ? THEME.success : THEME.textSecondary,
                        fontWeight: 600,
                        fontSize: "11px",
                        height: "22px",
                        minWidth: "36px"
                    }}
                />
            )
        },
        {
            field: "customerCountTrend",
            headerName: "MoM Customers",
            width: 130,
            align: "center",
            headerAlign: "center",
            renderCell: (params: GridRenderCellParams) => (
                <CurrentMonthCustomers count={params.value} />
            )
        },
        {
            field: "status",
            headerName: "Status",
            width: 125,
            renderCell: (params: GridRenderCellParams) => <StatusChip status={params.value || ""} />
        },
        {
            field: "customerFacingStatus",
            headerName: "Customer Facing Status",
            width: 180,
            renderCell: (params: GridRenderCellParams) => (
                <Tooltip title={params.value || ""}>
                    <Box
                        component="span"
                        sx={{
                            fontSize: "12px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: params.value ? THEME.textPrimary : THEME.textSecondary
                        }}
                    >
                        {params.value || "—"}
                    </Box>
                </Tooltip>
            )
        },
        {
            field: "priority",
            headerName: "Priority",
            width: 80,
            renderCell: (params: GridRenderCellParams) => <PriorityBadge priority={params.value} />
        },
        {
            field: "source",
            headerName: "Source",
            width: 80,
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value || "—"}
                    size="small"
                    sx={{
                        backgroundColor: params.value === "CRM" ? "#E5F1FB" :
                            params.value === "UAT" ? "#E8F5E9" :
                                params.value === "CSS" ? "#FFF3E0" : "#F5F5F5",
                        color: params.value === "CRM" ? THEME.primary :
                            params.value === "UAT" ? THEME.success :
                                params.value === "CSS" ? "#E65100" : THEME.textSecondary,
                        fontWeight: 500,
                        fontSize: "11px",
                        height: "22px"
                    }}
                />
            )
        },
        {
            field: "latestCreatedDate",
            headerName: "Created",
            width: 100,
            valueGetter: (value, row) => formatDate(row.latestCreatedDate),
            renderCell: (params: GridRenderCellParams) => (
                <Box component="span" sx={{ fontSize: "12px", color: params.value !== "—" ? THEME.textPrimary : THEME.textSecondary }}>
                    {params.value || "—"}
                </Box>
            )
        },
        {
            field: "lastChangedDate",
            headerName: "Last Changed",
            width: 100,
            valueGetter: (value, row) => formatDate(row.lastChangedDate),
            renderCell: (params: GridRenderCellParams) => (
                <Box component="span" sx={{ fontSize: "12px", color: params.value !== "—" ? THEME.textPrimary : THEME.textSecondary }}>
                    {params.value || "—"}
                </Box>
            )
        },
        ...buildTraceabilityColumns()
    ];

    // Initial loading state
    if (isLoading && !paginatedData) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, gap: 2 }}>
                <CircularProgress size={40} sx={{ color: THEME.primary }} />
                <Typography sx={{ color: THEME.textSecondary, fontSize: "14px" }}>Loading feature requests...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}>
            {/* Rich Search Bar */}
            <RichSearchBox
                value={searchText}
                onChange={setSearchText}
                onClear={() => setSearchText("")}
                placeholder="Search by Feature Request ID, Tenant Name, Tenant ID, Customer Name, Title, or Tags"
                showHint={searchText.length > 0 && searchText.length < 3}
            />

            {/* Filters Row */}
            <div className={mergeStyles({ margin: "-4px", display: "flex", flexWrap: "wrap", paddingBottom: 16, alignItems: "center" })}>
                <CTLFilterPill name="Product" options={filters.products} selectedValues={appliedFilters.product} onApply={(values) => onFilterChange("product", values || [])} urlParamKey="product" />
                <CTLFilterPill name="Submitter" options={filters.submitters} selectedValues={appliedFilters.submitter} onApply={(values) => onFilterChange("submitter", values || [])} urlParamKey="submitter" />
                <CTLFilterPill name="Status" options={filters.statuses} selectedValues={appliedFilters.status} onApply={(values) => onFilterChange("status", values || [])} urlParamKey="status" />
                <CTLFilterPill name="Priority" options={filters.priorities} selectedValues={appliedFilters.priority} onApply={(values) => onFilterChange("priority", values || [])} urlParamKey="priority" />
                <CTLFilterPill name="Industry" options={filters.industries} selectedValues={appliedFilters.industry} onApply={(values) => onFilterChange("industry", values || [])} urlParamKey="industry" />
                <CTLFilterPill name="Vertical" options={filters.verticals} selectedValues={appliedFilters.vertical} onApply={(values) => onFilterChange("vertical", values || [])} urlParamKey="vertical" />
                <CTLFilterPill name="Region" options={filters.regions} selectedValues={appliedFilters.region} onApply={(values) => onFilterChange("region", values || [])} urlParamKey="region" />
                <CTLFilterPill name="Source" options={filters.sources} selectedValues={appliedFilters.source} onApply={(values) => onFilterChange("source", values || [])} urlParamKey="source" />
                <CTLSearchableFilterPill name="Feature Category" options={filters.featureCategories || []} selectedValues={appliedFilters.featureCategory} onApply={(values) => onFilterChange("featureCategory", values || [])} maxVisibleItems={50} />
                <CTLSearchableFilterPill name="Case Number" options={filters.caseNumbers || []} selectedValues={appliedFilters.caseNumber} onApply={(values) => onFilterChange("caseNumber", values || [])} maxVisibleItems={50} />
                <CTLDateRangeFilterPill
                    name="Created Date"
                    onApply={(from, to) => {
                        onDateRangeFilterChange("createdDateFrom", "createdDateTo", from, to);
                    }}
                />
                <CTLDateRangeFilterPill
                    name="Last Updated Date"
                    onApply={(from, to) => {
                        onDateRangeFilterChange("lastChangedDateFrom", "lastChangedDateTo", from, to);
                    }}
                />
                <ShareFilterButton />
            </div>

            {/* Data Grid */}
            <Paper
                elevation={0}
                sx={{
                    border: `1px solid ${THEME.border}`,
                    borderRadius: "8px",
                    overflow: "hidden",
                    flex: 1,
                    minHeight: 200,
                    maxHeight: "calc(100vh - 450px)",
                    display: "flex",
                    flexDirection: "column",
                    "& .MuiDataGrid-root": {
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        border: "none"
                    },
                    "& .MuiDataGrid-main": {
                        flex: 1,
                        overflow: "auto",
                        minHeight: 0
                    },
                    "& .MuiDataGrid-footerContainer": {
                        minHeight: 52,
                        flexShrink: 0,
                        borderTop: `1px solid ${THEME.border}`
                    }
                }}
            >
                <DataGrid
                    rows={paginatedData?.featureRequests || []}
                    columns={columns}
                    loading={isLoading || isFetching}
                    density="compact"
                    getRowId={(row) => row.featureRequestIdWithSource}
                    paginationMode="server"
                    rowCount={paginatedData?.totalCount || 0}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[25, 50, 100]}
                    sortingMode="server"
                    sortModel={[{ field: sortModel.field, sort: sortModel.sort }]}
                    onSortModelChange={(model) => {
                        if (model.length > 0) {
                            setSortModel({ field: model[0].field, sort: model[0].sort || "asc" });
                        } else {
                            setSortModel((prev) => ({ ...prev, sort: prev.sort === "asc" ? "desc" : "asc" }));
                        }
                    }}
                    disableRowSelectionOnClick

                    // Column filter runs server-side (maps to *Contains params) so it searches the whole
                    // dataset; the client-side quick filter stays off since it only sees the loaded page.
                    filterMode="server"
                    onFilterModelChange={handleColumnFilterChange}

                    // Prefetch panel data on row hover
                    slotProps={{
                        row: {
                            onMouseEnter: (event: React.MouseEvent<HTMLDivElement>) => {
                                const rowId = event.currentTarget.getAttribute("data-id");
                                if (rowId) handleRowHover(rowId);
                            },
                            onMouseLeave: handleRowHoverEnd
                        }
                    }}

                    slots={{
                        toolbar: () => (
                            <CustomDataGridToolbar
                                filterModel={{ items: [] }}
                                toolbarConfig={{ disableGridToolbarQuickFilter: true }}
                                exportCsvButtonProps={{
                                    buttonLabel: isExporting ? "Exporting..." : "Export All",
                                    fileNamePrefix: "feature-requests-review",
                                    tooltip: "Export all filtered data to CSV",
                                    onCustomExport: handleExportAll,
                                    isExporting: isExporting
                                }}
                            />
                        ),
                        loadingOverlay: () => (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", width: "100%", backgroundColor: "rgba(255, 255, 255, 0.95)" }}>
                                <CircularProgress size={40} sx={{ color: THEME.primary, mb: 2 }} />
                                <Typography sx={{ color: THEME.textPrimary, fontSize: "14px", fontWeight: 500 }}>Loading data...</Typography>
                            </Box>
                        ),
                        noRowsOverlay: () => (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", py: 4, background: "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(250,251,252,1) 100%)" }}>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", maxWidth: 320 }}>
                                    <Box sx={{ width: 80, height: 80, borderRadius: "20px", background: "linear-gradient(135deg, #F0F4FF 0%, #E8F0FE 100%)", display: "flex", alignItems: "center", justifyContent: "center", mb: 3, boxShadow: "0 4px 12px rgba(0, 90, 158, 0.08)" }}>
                                        <Search20Regular style={{ color: THEME.primary, width: 36, height: 36, opacity: 0.8 }} />
                                    </Box>
                                    <Typography sx={{ color: THEME.textPrimary, fontSize: "16px", fontWeight: 600, mb: 1 }}>No Results Found</Typography>
                                    <Typography sx={{ color: THEME.textSecondary, fontSize: "13px", textAlign: "center", lineHeight: 1.5 }}>We couldn't find any feature requests matching your criteria. Try adjusting your filters or search terms.</Typography>
                                </Box>
                            </Box>
                        )
                    }}
                    sx={{
                        border: "none",
                        fontSize: "12px",
                        "& .MuiDataGrid-columnHeaders": { backgroundColor: "#F8F9FA", borderBottom: `1px solid ${THEME.border}` },
                        "& .MuiDataGrid-columnHeaderTitle": { fontSize: "12px", fontWeight: 600 },
                        "& .MuiDataGrid-cell": { borderBottom: `1px solid ${THEME.border}`, fontSize: "12px" },
                        "& .MuiDataGrid-row:hover": { backgroundColor: `${THEME.primary}08` },
                        "& .MuiDataGrid-menuIconButton": { color: "white" },
                        "& .MuiDataGrid-overlayWrapper": { backgroundColor: "rgba(255, 255, 255, 0.98)" }
                    }}
                />
            </Paper>
            {/* Explore Data Button Footer - Matches CustomCard footer style */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    minHeight: 45,
                    borderTop: `2px solid ${THEME.border}`,
                    backgroundColor: THEME.surface,
                    borderRadius: "0 0 8px 8px",
                    border: `1px solid ${THEME.border}`,
                    borderTopWidth: 0,
                    px: 1.5
                }}
            >
                <CTLExploreDataButton
                    query={CTL_FEATURE_EVIDENCES_QUERY}
                    chartTitle="All Requests Table"
                />
            </Box>
        </Box>
    );
};

/**
 * Main Close The Loop Page Component
 */
const CloseTheLoopPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<string>("track-requests");
    const [activeView, setActiveView] = useState<ViewType>(
        (searchParams.get("view") as ViewType) || "submissions"
    );
    const [activeReviewSubTab, setActiveReviewSubTab] = useState<string>("all-requests");

    // Initialize filters from URL on page load
    const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>(() => {
        try {
            const filtersParam = searchParams.get("filters");
            if (filtersParam) {
                const decoded = JSON.parse(atob(filtersParam));
                // Convert semicolon-separated values to arrays
                const filters: Record<string, string[]> = {};
                Object.entries(decoded).forEach(([key, value]) => {
                    if (typeof value === "string") {
                        filters[key] = value.split(";").filter(v => v.trim());
                    }
                });
                return filters;
            }
        } catch {
            // Silently fail if URL params are malformed
        }
        return {};
    });

    // Feature details panel state
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);

    // Feedback modal state
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

    // Selected customer state for filter banner
    const [selectedCustomerFilter, setSelectedCustomerFilter] = useState<ICustomerSummary | null>(null);

    // Handle selecting a customer from Customer View - redirect to the Explore Customers
    // Customer View (Feature Request tab) for that tenant.
    const handleSelectCustomer = useCallback((customer: ICustomerSummary) => {
        if (!customer.tenantId) return;
        navigate(
            `/customer?id=${encodeURIComponent(customer.tenantId)}&tab=${CustomerViewTabs.FeatureRequest}&from=closetheloop`
        );
    }, [navigate]);

    // Handle selecting a TPID from Customer View - redirect to Feature Request > View by TPID.
    const handleSelectCustomerByTPID = useCallback((customer: ICustomerSummary) => {
        if (!customer.tenantId || !customer.tpid) return;
        navigate(
            `/customer?id=${encodeURIComponent(customer.tenantId)}&tab=${CustomerViewTabs.FeatureRequestViewByTPID}&tpid=${encodeURIComponent(customer.tpid)}&from=closetheloop`
        );
    }, [navigate]);

    // Handle clearing customer filter
    const handleClearCustomerFilter = useCallback(() => {
        setSelectedCustomerFilter(null);
        setAppliedFilters(prev => {
            const { customerContains, ...rest } = prev;
            return rest;
        });
    }, []);

    // Handle opening feature details panel - takes featureRequestIdWithSource string
    const handleOpenDetails = useCallback((featureRequestIdWithSource: string) => {
        setSelectedFeatureId(featureRequestIdWithSource);
        setIsPanelOpen(true);
        // Update URL with panel param for shareability
        const newParams = new URLSearchParams(searchParams);
        newParams.set("panel", featureRequestIdWithSource);
        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);

    // Handle closing feature details panel
    const handleClosePanel = useCallback(() => {
        setIsPanelOpen(false);
        // Remove panel param from URL
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("panel");
        setSearchParams(newParams);
        // Clear selection after animation completes
        setTimeout(() => {
            setSelectedFeatureId(null);
        }, 300);
    }, [searchParams, setSearchParams]);

    // Open panel from URL parameter on page load (only once)
    const initialPanelLoaded = useRef(false);
    useEffect(() => {
        if (initialPanelLoaded.current) return;
        const panelId = searchParams.get("panel");
        if (panelId) {
            setSelectedFeatureId(panelId);
            setIsPanelOpen(true);
            initialPanelLoaded.current = true;
        }
    }, [searchParams]);

    // Fetch customers data (lightweight, only for CustomerView)
    const {
        data: customersData,
        isLoading: isLoadingCustomers
    } = useGetCustomersQuery(undefined, {
        skip: activeView !== "customers"
    });

    // Fetch filters separately (fast response, used immediately)
    const { data: filtersData } = useGetFiltersQuery();

    // Prefetch data in background for faster subsequent navigation
    const prefetchFilters = useCloseTheLoopPrefetch("getFilters");
    const prefetchCustomers = useCloseTheLoopPrefetch("getCustomers");
    const prefetchOverview = useCloseTheLoopPrefetch("getOverview");
    const prefetchRequests = useCloseTheLoopPrefetch("getPaginatedFeatureRequests");

    // Trigger prefetch on page mount (background, doesn't block)
    useEffect(() => {
        // Prefetch all commonly used data on page load
        prefetchFilters(undefined, { force: false });
        prefetchCustomers(undefined, { force: false });
        prefetchOverview(undefined, { force: false });
        prefetchRequests({ page: 1, pageSize: 25, sortBy: "lastChangedDate", sortDirection: "desc" }, { force: false });
    }, [prefetchFilters, prefetchCustomers, prefetchOverview, prefetchRequests]);

    // Update URL when view changes (preserve panel param)
    useEffect(() => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("view", activeView);
        setSearchParams(newParams);
    }, [activeView]);

    // Track page view
    useEffect(() => {
        logPageView("Close The Loop", window.location.href, { Category: "CloseTheLoop" });
        startPageLoadTimeTracker("Close The Loop");

        return () => {
            stopPageLoadTimeTracker("Close The Loop", window.location.href);
        };
    }, []);

    // Track main tab views - fires whenever a tab becomes active (matches Customer/Product details pages)
    useEffect(() => {
        const tabLabels: Record<string, string> = {
            "track-requests": "Track Requests",
            "review-analyze": "Review & Analyze",
            "prioritize-decide": "Prioritize & Decide",
            "executive-overview": "Executive Overview"
        };
        logEvent(`${tabLabels[activeTab] ?? activeTab} Tab`, "Close The Loop Page", "CloseTheLoopPage");
    }, [activeTab]);

    // Track Track Requests sub-view (All Submissions / Customer View) when active
    useEffect(() => {
        if (activeTab !== "track-requests") return;
        const viewLabels: Record<string, string> = {
            submissions: "Feature Requests",
            "dcr-requests": "DCR Requests",
            customers: "Customer View"
        };
        logEvent(`${viewLabels[activeView] ?? activeView} Tab`, "Close The Loop Page", "CloseTheLoopPage");
    }, [activeTab, activeView]);

    // Track Review & Analyze sub-tab (Overview / Intake to Triage / Data Quality / All Requests) when active
    useEffect(() => {
        if (activeTab !== "review-analyze") return;
        const reviewSubTabLabels: Record<string, string> = {
            "overview": "Overview",
            "intake-to-triage": "Intake to Triage",
            "data-quality": "Data Quality",
            "all-requests": "Feature Requests"
        };
        logEvent(`${reviewSubTabLabels[activeReviewSubTab] ?? activeReviewSubTab} Tab`, "Close The Loop Page", "CloseTheLoopPage");
    }, [activeTab, activeReviewSubTab]);

    const handleFilterChange = useCallback((filterKey: string, values: string[]) => {
        setAppliedFilters((prev) => ({
            ...prev,
            [filterKey]: values
        }));
    }, []);

    const handleDateRangeFilterChange = useCallback((fromKey: string, toKey: string, fromValue: string, toValue: string) => {
        setAppliedFilters((prev) => ({
            ...prev,
            [fromKey]: fromValue ? [fromValue] : [],
            [toKey]: toValue ? [toValue] : []
        }));
    }, []);

    // Filters from the fast filters endpoint
    const filters = filtersData || {
        statuses: [],
        priorities: [],
        products: [],
        submitters: [],
        industries: [],
        verticals: [],
        regions: [],
        stages: [],
        etas: [],
        sources: [],
        featureCategories: [],
        productFamilies: [],
        caseNumbers: []
    };

    // Main navigation tabs (placeholders for now)
    const mainTabs = [
        { id: "track-requests", label: "Track Requests" },
        { id: "review-analyze", label: "Review & Analyze" },
        { id: "prioritize-decide", label: "Prioritize & Decide" },
        { id: "executive-overview", label: "Executive Overview" }
    ];

    return (
        <Box
            sx={{
                height: "100vh",
                overflow: "hidden",
                backgroundColor: THEME.background,
                fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
                display: "flex",
                flexDirection: "column"
            }}
        >
            {/* Rich Header with Navigation Tabs */}
            <Paper
                elevation={2}
                sx={{
                    backgroundColor: THEME.surface,
                    borderBottom: `1px solid ${THEME.border}`,
                    flexShrink: 0
                }}
            >
                {/* Header Title Section */}
                <Box
                    sx={{
                        px: 3,
                        py: 3,
                        borderBottom: `1px solid ${THEME.border}`,
                        position: "relative"
                    }}
                >
                    {/* Feedback Button - Top Right */}
                    <Tooltip title="Share your feedback">
                        <Button
                            onClick={() => setIsFeedbackOpen(true)}
                            startIcon={<ChatBubblesQuestion24Regular />}
                            sx={{
                                position: "absolute",
                                top: 16,
                                right: 24,
                                textTransform: "none",
                                fontWeight: 500,
                                fontSize: "13px",
                                px: 2,
                                py: 0.75,
                                borderRadius: "8px",
                                color: THEME.primary,
                                backgroundColor: `${THEME.primary}10`,
                                border: `1px solid ${THEME.primary}30`,
                                "&:hover": {
                                    backgroundColor: `${THEME.primary}20`,
                                    borderColor: THEME.primary
                                }
                            }}
                        >
                            Feedback
                        </Button>
                    </Tooltip>

                    <Box sx={{ textAlign: "center" }}>
                        <Typography
                            variant="h4"
                            fontWeight={700}
                            sx={{
                                color: THEME.textPrimary,
                                letterSpacing: "-0.5px"
                            }}
                        >
                            Close the Loop
                        </Typography>
                        <Typography variant="body2" sx={{ color: THEME.textSecondary, mt: 0.5 }}>
                            Track, analyze, and manage customer feature requests
                        </Typography>
                    </Box>
                </Box>

                {/* Navigation Tabs */}
                <Box sx={{ px: 3, backgroundColor: THEME.surface }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, newValue) => {
                            if (activeTab !== newValue) {
                                // Preserve filters when switching between track-requests and review-analyze
                                const preserveFiltersTabs = ["track-requests", "review-analyze"];
                                if (!preserveFiltersTabs.includes(activeTab) || !preserveFiltersTabs.includes(newValue)) {
                                    setAppliedFilters({});
                                }
                                setSelectedCustomerFilter(null);
                            }
                            setActiveTab(newValue);
                        }}
                        centered
                        sx={{
                            "& .MuiTabs-flexContainer": {
                                gap: "8px"
                            },
                            "& .MuiTab-root": {
                                color: THEME.textSecondary,
                                textTransform: "none",
                                fontWeight: 500,
                                fontSize: "14px",
                                minHeight: "48px",
                                px: 3,
                                borderRadius: "8px 8px 0 0",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    color: THEME.primary,
                                    backgroundColor: `${THEME.primary}08`
                                },
                                "&.Mui-selected": {
                                    color: THEME.primary,
                                    fontWeight: 600,
                                    backgroundColor: `${THEME.primary}10`
                                }
                            },
                            "& .MuiTabs-indicator": {
                                backgroundColor: THEME.primary,
                                height: 3,
                                borderRadius: "3px 3px 0 0"
                            }
                        }}
                    >
                        {mainTabs.map((tab) => (
                            <Tab key={tab.id} value={tab.id} label={tab.label} />
                        ))}
                    </Tabs>
                </Box>
            </Paper>

            {/* Main Content */}
            <Box sx={{ p: 3, flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column" }}>
                {activeTab === "track-requests" && (
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                        {/* Page Title */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h5" fontWeight={600} color={THEME.textPrimary}>
                                Track Requests
                            </Typography>
                        </Box>

                        {/* Sub-tabs: All Submissions / Customer View */}
                        <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                            <Button
                                onClick={() => {
                                    if (activeView !== "submissions") {
                                        setAppliedFilters({});
                                        setSelectedCustomerFilter(null);
                                    }
                                    setActiveView("submissions");
                                }}
                                startIcon={<GridDots20Regular />}
                                sx={{
                                    textTransform: "none",
                                    borderRadius: "20px",
                                    px: 2,
                                    border: "1px solid",
                                    transition: "none",
                                    backgroundColor: activeView === "submissions" ? THEME.primary : "#FFFFFF",
                                    borderColor: activeView === "submissions" ? THEME.primary : THEME.border,
                                    color: activeView === "submissions" ? "#FFFFFF" : THEME.textSecondary,
                                    "&:hover": {
                                        backgroundColor: activeView === "submissions" ? THEME.primaryDark : "#F5F5F5",
                                        borderColor: activeView === "submissions" ? THEME.primaryDark : THEME.primary,
                                        color: activeView === "submissions" ? "#FFFFFF" : THEME.primary
                                    }
                                }}
                            >
                                Feature Requests
                            </Button>
                            <Button
                                onClick={() => {
                                    if (activeView !== "customers") {
                                        setAppliedFilters({});
                                        setSelectedCustomerFilter(null);
                                    }
                                    setActiveView("customers");
                                }}
                                startIcon={<People20Regular />}
                                sx={{
                                    textTransform: "none",
                                    borderRadius: "20px",
                                    px: 2,
                                    border: "1px solid",
                                    transition: "none",
                                    backgroundColor: activeView === "customers" ? THEME.primary : "#FFFFFF",
                                    borderColor: activeView === "customers" ? THEME.primary : THEME.border,
                                    color: activeView === "customers" ? "#FFFFFF" : THEME.textSecondary,
                                    "&:hover": {
                                        backgroundColor: activeView === "customers" ? THEME.primaryDark : "#F5F5F5",
                                        borderColor: activeView === "customers" ? THEME.primaryDark : THEME.primary,
                                        color: activeView === "customers" ? "#FFFFFF" : THEME.primary
                                    }
                                }}
                            >
                                Customer View
                            </Button>
                            <Button
                                onClick={() => {
                                    if (activeView !== "dcr-requests") {
                                        setAppliedFilters({});
                                        setSelectedCustomerFilter(null);
                                    }
                                    setActiveView("dcr-requests");
                                }}
                                startIcon={<GridDots20Regular />}
                                sx={{
                                    textTransform: "none",
                                    borderRadius: "20px",
                                    px: 2,
                                    border: "1px solid",
                                    transition: "none",
                                    backgroundColor: activeView === "dcr-requests" ? THEME.primary : "#FFFFFF",
                                    borderColor: activeView === "dcr-requests" ? THEME.primary : THEME.border,
                                    color: activeView === "dcr-requests" ? "#FFFFFF" : THEME.textSecondary,
                                    "&:hover": {
                                        backgroundColor: activeView === "dcr-requests" ? THEME.primaryDark : "#F5F5F5",
                                        borderColor: activeView === "dcr-requests" ? THEME.primaryDark : THEME.primary,
                                        color: activeView === "dcr-requests" ? "#FFFFFF" : THEME.primary
                                    }
                                }}
                            >
                                DCR Requests
                            </Button>
                        </Box>

                        {/* Sub-tab description */}
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            px: 2.5,
                            py: 1,
                            mb: 2,
                            borderRadius: "6px",
                            backgroundColor: "#FFF9C4",
                            borderLeft: "4px solid #FBC02D"
                        }}>
                            <Typography variant="body2" sx={{ color: "#5D4E00", fontWeight: 500 }}>
                                {activeView === "submissions" && "Track customer requests by status, lifecycle, and source with filtering and shareable views."}
                                {activeView === "dcr-requests" && "Review every DCR, including requests not converted to features, with conversion and feature association shown separately."}
                                {activeView === "customers" && "Search by tenant to view relevant requests, status, and updates."}
                            </Typography>
                        </Box>

                        {/* View Content - Both rendered, instant switch with no transition delay */}
                        <Box sx={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                            <Box
                                sx={{
                                    position: activeView === "submissions" ? "relative" : "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: activeView === "submissions" ? "auto" : 0,
                                    visibility: activeView === "submissions" ? "visible" : "hidden",
                                    zIndex: activeView === "submissions" ? 1 : 0,
                                    display: activeView === "submissions" ? "flex" : "none",
                                    flexDirection: "column",
                                    flex: activeView === "submissions" ? 1 : "none",
                                    minHeight: 0
                                }}
                            >
                                {/* Customer Filter Banner */}
                                {selectedCustomerFilter && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            mb: 2,
                                            p: 1.5,
                                            backgroundColor: "#E3F2FD",
                                            borderRadius: "8px",
                                            border: "1px solid #90CAF9"
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            <People20Regular style={{ color: THEME.primary }} />
                                            <Box>
                                                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: THEME.textPrimary }}>
                                                    Filtered by: {selectedCustomerFilter.customerName || selectedCustomerFilter.tenantName}
                                                </Typography>
                                                <Typography sx={{ fontSize: "11px", color: THEME.textSecondary }}>
                                                    TPID: {selectedCustomerFilter.tpid || "—"} • Tenant: {selectedCustomerFilter.tenantId || "—"}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={handleClearCustomerFilter}
                                            sx={{
                                                textTransform: "none",
                                                borderColor: THEME.primary,
                                                color: THEME.primary,
                                                fontSize: "12px",
                                                px: 2,
                                                "&:hover": {
                                                    backgroundColor: "#E3F2FD",
                                                    borderColor: THEME.primaryDark
                                                }
                                            }}
                                        >
                                            Clear Filter
                                        </Button>
                                    </Box>
                                )}
                                <AllSubmissionsView
                                    filters={filters}
                                    appliedFilters={appliedFilters}
                                    onFilterChange={handleFilterChange}
                                    onDateRangeFilterChange={handleDateRangeFilterChange}
                                    onOpenDetails={handleOpenDetails}
                                    extraTopOffset={selectedCustomerFilter ? 72 : 0}
                                />
                            </Box>
                            <Box
                                sx={{
                                    position: activeView === "dcr-requests" ? "relative" : "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: activeView === "dcr-requests" ? "auto" : 0,
                                    visibility: activeView === "dcr-requests" ? "visible" : "hidden",
                                    zIndex: activeView === "dcr-requests" ? 1 : 0,
                                    display: activeView === "dcr-requests" ? "flex" : "none",
                                    flexDirection: "column",
                                    flex: activeView === "dcr-requests" ? 1 : "none",
                                    minHeight: 0
                                }}
                            >
                                <DCRRequests onOpenDetails={handleOpenDetails} />
                            </Box>
                            <Box
                                sx={{
                                    position: activeView === "customers" ? "relative" : "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: activeView === "customers" ? "auto" : 0,
                                    visibility: activeView === "customers" ? "visible" : "hidden",
                                    zIndex: activeView === "customers" ? 1 : 0,
                                    display: activeView === "customers" ? "flex" : "none",
                                    flexDirection: "column",
                                    flex: activeView === "customers" ? 1 : "none",
                                    minHeight: 0
                                }}
                            >
                                <CustomerView
                                    data={customersData?.customers || []}
                                    isLoading={isLoadingCustomers}
                                    onSelectCustomer={handleSelectCustomer}
                                    onSelectCustomerByTPID={handleSelectCustomerByTPID}
                                />
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Placeholder for other tabs */}
                {activeTab !== "track-requests" && activeTab !== "review-analyze" && activeTab !== "prioritize-decide" && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 400,
                            backgroundColor: THEME.surface,
                            borderRadius: "12px",
                            border: `1px solid ${THEME.border}`
                        }}
                    >
                        <Typography variant="h6" color={THEME.textSecondary}>
                            {mainTabs.find((t) => t.id === activeTab)?.label} - Coming Soon
                        </Typography>
                    </Box>
                )}

                {/* Prioritize & Decide Tab */}
                {activeTab === "prioritize-decide" && (
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                        {/* Page Title */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h5" fontWeight={600} color={THEME.textPrimary}>
                                Prioritize & Decide
                            </Typography>
                            <Typography variant="body2" color={THEME.textSecondary} sx={{ mt: 0.5 }}>
                                Prioritize feature requests across the full portfolio — demand, delivery pipeline health, and time-to-deliver.
                            </Typography>
                        </Box>
                        <Box sx={{ overflowY: "auto", flex: 1, pr: 1 }}>
                            <PrioritizeAndDecide onOpenDetails={handleOpenDetails} />
                        </Box>
                    </Box>
                )}

                {/* Review & Analyze Tab - All Requests View */}
                {activeTab === "review-analyze" && (
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                        {/* Page Title */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h5" fontWeight={600} color={THEME.textPrimary}>
                                Review & Analyze
                            </Typography>
                        </Box>

                        {/* Sub-tabs for Review & Analyze */}
                        <Box sx={{ display: "flex", gap: 1, mb: 0.5, alignItems: "center", justifyContent: "space-between" }}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                    onClick={() => setActiveReviewSubTab("overview")}
                                    sx={{
                                        textTransform: "none",
                                        borderRadius: "20px",
                                        px: 2,
                                        border: "1px solid",
                                        transition: "none",
                                        backgroundColor: activeReviewSubTab === "overview" ? THEME.primary : "#FFFFFF",
                                        borderColor: activeReviewSubTab === "overview" ? THEME.primary : THEME.border,
                                        color: activeReviewSubTab === "overview" ? "#FFFFFF" : THEME.textSecondary,
                                        "&:hover": {
                                            backgroundColor: activeReviewSubTab === "overview" ? THEME.primaryDark : "#F5F5F5",
                                            borderColor: activeReviewSubTab === "overview" ? THEME.primaryDark : THEME.primary,
                                            color: activeReviewSubTab === "overview" ? "#FFFFFF" : THEME.primary
                                        }
                                    }}
                                >
                                    Overview
                                </Button>
                                <Button
                                    onClick={() => setActiveReviewSubTab("intake-to-triage")}
                                    sx={{
                                        textTransform: "none",
                                        borderRadius: "20px",
                                        px: 2,
                                        border: "1px solid",
                                        transition: "none",
                                        backgroundColor: activeReviewSubTab === "intake-to-triage" ? THEME.primary : "#FFFFFF",
                                        borderColor: activeReviewSubTab === "intake-to-triage" ? THEME.primary : THEME.border,
                                        color: activeReviewSubTab === "intake-to-triage" ? "#FFFFFF" : THEME.textSecondary,
                                        "&:hover": {
                                            backgroundColor: activeReviewSubTab === "intake-to-triage" ? THEME.primaryDark : "#F5F5F5",
                                            borderColor: activeReviewSubTab === "intake-to-triage" ? THEME.primaryDark : THEME.primary,
                                            color: activeReviewSubTab === "intake-to-triage" ? "#FFFFFF" : THEME.primary
                                        }
                                    }}
                                >
                                    Intake to Triage
                                </Button>
                                <Button
                                    onClick={() => setActiveReviewSubTab("data-quality")}
                                    sx={{
                                        textTransform: "none",
                                        borderRadius: "20px",
                                        px: 2,
                                        border: "1px solid",
                                        transition: "none",
                                        backgroundColor: activeReviewSubTab === "data-quality" ? THEME.primary : "#FFFFFF",
                                        borderColor: activeReviewSubTab === "data-quality" ? THEME.primary : THEME.border,
                                        color: activeReviewSubTab === "data-quality" ? "#FFFFFF" : THEME.textSecondary,
                                        "&:hover": {
                                            backgroundColor: activeReviewSubTab === "data-quality" ? THEME.primaryDark : "#F5F5F5",
                                            borderColor: activeReviewSubTab === "data-quality" ? THEME.primaryDark : THEME.primary,
                                            color: activeReviewSubTab === "data-quality" ? "#FFFFFF" : THEME.primary
                                        }
                                    }}
                                >
                                    Data Quality
                                </Button>
                                <Button
                                    onClick={() => setActiveReviewSubTab("all-requests")}
                                    startIcon={<GridDots20Regular />}
                                    sx={{
                                        textTransform: "none",
                                        borderRadius: "20px",
                                        px: 2,
                                        border: "1px solid",
                                        transition: "none",
                                        backgroundColor: activeReviewSubTab === "all-requests" ? THEME.primary : "#FFFFFF",
                                        borderColor: activeReviewSubTab === "all-requests" ? THEME.primary : THEME.border,
                                        color: activeReviewSubTab === "all-requests" ? "#FFFFFF" : THEME.textSecondary,
                                        "&:hover": {
                                            backgroundColor: activeReviewSubTab === "all-requests" ? THEME.primaryDark : "#F5F5F5",
                                            borderColor: activeReviewSubTab === "all-requests" ? THEME.primaryDark : THEME.primary,
                                            color: activeReviewSubTab === "all-requests" ? "#FFFFFF" : THEME.primary
                                        }
                                    }}
                                >
                                    Feature Requests
                                </Button>
                            </Box>
                        </Box>

                        {/* Sub-tab description */}
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            px: 2.5,
                            py: 1,
                            mb: 2,
                            borderRadius: "6px",
                            backgroundColor: "#FFF9C4",
                            borderLeft: "4px solid #FBC02D"
                        }}>
                            <Typography variant="body2" sx={{ color: "#5D4E00", fontWeight: 500 }}>
                                {activeReviewSubTab === "overview" && "Monitor feature request demand, trends, and pipeline health."}
                                {activeReviewSubTab === "intake-to-triage" && "Evaluate DCR to CRM automation to uncover efficiency gaps and delays."}
                                {activeReviewSubTab === "data-quality" && "AI insights to assess feature request quality, completeness, and duplication."}
                                {activeReviewSubTab === "all-requests" && "Analyze requests to understand customer demand and map to engineering work."}
                            </Typography>
                        </Box>

                        {/* All Requests View with Trend Column */}
                        {activeReviewSubTab === "all-requests" && (
                            <AllRequestsView
                                filters={filters}
                                appliedFilters={appliedFilters}
                                onFilterChange={handleFilterChange}
                                onDateRangeFilterChange={handleDateRangeFilterChange}
                                onOpenDetails={handleOpenDetails}
                            />
                        )}

                        {activeReviewSubTab === "overview" && (
                            <Box sx={{ overflowY: "auto", flex: 1, pr: 1 }}>
                                <Overview />
                            </Box>
                        )}

                        {activeReviewSubTab === "intake-to-triage" && (
                            <Box sx={{ overflowY: "auto", flex: 1, pr: 1 }}>
                                <IntakeToTriage />
                            </Box>
                        )}

                        {/* Coming Soon placeholder for other sub-tabs */}
                        {activeReviewSubTab !== "all-requests" && activeReviewSubTab !== "overview" && activeReviewSubTab !== "intake-to-triage" && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minHeight: 400,
                                    backgroundColor: THEME.surface,
                                    borderRadius: "12px",
                                    border: `1px solid ${THEME.border}`
                                }}
                            >
                                <Typography variant="h6" color={THEME.textSecondary}>
                                    {activeReviewSubTab === "data-quality" && "Data Quality - Coming Soon"}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Feature Details Panel */}
            <FeatureDetailsPanel
                isOpen={isPanelOpen}
                onClose={handleClosePanel}
                featureRequestIdWithSource={selectedFeatureId}
                showCustomersSection={activeTab === "review-analyze" || activeTab === "prioritize-decide"}
            />

            {/* Feedback Modal */}
            <FeedbackFormModal
                isOpen={isFeedbackOpen}
                onClose={() => setIsFeedbackOpen(false)}
            />
        </Box >
    );
};

export default CloseTheLoopPage;
